import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Star, CheckCircle, Edit2, Plus, Trash2, ArrowUp, ArrowDown, FileCheck } from 'lucide-react';
import { PORTAL_EMOJI } from '../utils/portalEmojis';
import BackButton from '../components/BackButton';
import PortalBackground from '../components/PortalBackground';
import BBALoader from '../components/BBALoader';
import {
  CARD, SUBCARD, INPUT, ICON_BTN, DANGER_ICON_BTN, DASHED_ADD, TONES,
  IconTile, SectionHeader, EditToolbar, StatTile, ScoreRing, ThresholdMetric, StepCards, ModalShell
} from '../components/assessment/AssessmentAnalysisUi';
import StartSidebar from '../components/StartSidebar';
import ProgramDetail, { CUSTOM_PROGRAM, CUSTOM_TIER } from './ProgramDetail';
import WebsiteAuditOverviewSection from '../components/assessment/WebsiteAuditOverviewSection';
import StartFlowPreview from '../components/assessment/StartFlowPreview';
import ProcessFlowPreview from '../components/assessment/ProcessFlowPreview';
import { db } from '../firebase';
import { doc, onSnapshot, collection, query, where, getDocs, getDoc, updateDoc } from 'firebase/firestore';
import { updateAccount, getCrmCompanyLogoDisplayUrl } from '../services/firestoreService';
import { isRetiredProgram } from '../utils/retiredPrograms';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';


/** Default Key Implementations cards when `immediateLevers` is not set on the account yet. */
const DEFAULT_IMMEDIATE_LEVERS = [
  { emoji: '💎', header: 'PD/PM Model', description: '', department: 'sales' },
  { emoji: '⚙️', header: 'Systems Implementation', description: '', department: 'operations' },
  { emoji: '💰', header: 'Financial Clarity & Control', description: '', department: 'finance' },
  { emoji: '📈', header: 'Team Scaling Breakdown', description: '', department: 'operations' },
  { emoji: '🤖', header: 'JobTread Optimization & Automations', description: '', department: 'technology' },
  { emoji: '💻', header: 'Website & Digital Presence', description: '', department: 'marketing' }
];

function parseImmediateLevers(ll) {
  if (ll === undefined || ll === null) {
    return DEFAULT_IMMEDIATE_LEVERS.map((c) => ({ ...c }));
  }
  if (Array.isArray(ll) && ll.length > 0) {
    return ll.map((c) => ({
      emoji: c.emoji || '',
      header: c.header || '',
      description: c.description || '',
      department: c.department || ''
    }));
  }
  if (typeof ll === 'object' && !Array.isArray(ll)) {
    const depts = ['sales', 'operations', 'finance', 'marketing', 'technology'];
    const cards = [];
    depts.forEach((dept) => {
      const items = Array.isArray(ll[dept]) ? ll[dept] : (ll[dept]?.text || ll[dept]?.emoji ? [ll[dept]] : []);
      items.forEach((item) =>
        cards.push({
          emoji: item.emoji || '',
          header: dept.charAt(0).toUpperCase() + dept.slice(1),
          description: item.text || item.description || '',
          department: dept
        })
      );
    });
    return cards;
  }
  return [];
}

/** Default Implementation Results rows when `implementationResults` is not set on the account yet. */
const DEFAULT_IMPLEMENTATION_RESULTS = [
  '✅ A Systemized Business',
  '✅ Growing Year Over Year',
  '✅ Strategically Scaling',
  '✅ Optimized, Automated, & Efficient',
  '✅ Fully Aware of Financials',
  '✅ Generating More Leads'
];

function parseImplementationResults(ir) {
  if (ir === undefined || ir === null) {
    return [...DEFAULT_IMPLEMENTATION_RESULTS];
  }
  if (Array.isArray(ir)) {
    return ir.map((row) => (typeof row === 'string' ? row : String(row ?? '')));
  }
  return [];
}

/** Default Goals when `goals` is not set on the account yet. */
const DEFAULT_GOALS = [
  { header: 'Short Term', goal: '⭐ Systemize The Business' },
  { header: '3 Year', goal: '⭐ Team In Place' },
  { header: 'Long Term', goal: '⭐ Management Run' }
];

function parseGoals(g) {
  if (g === undefined || g === null) {
    return DEFAULT_GOALS.map((c) => ({ ...c }));
  }
  if (Array.isArray(g) && g.length > 0) {
    return g.map((c) => ({ header: c.header || '', goal: c.goal || '' }));
  }
  if (typeof g === 'object' && !Array.isArray(g)) {
    const cards = [];
    if (g.oneYear) cards.push({ header: '1 Year', goal: g.oneYear });
    if (g.threeYear) cards.push({ header: '3 Year', goal: g.threeYear });
    if (g.fiveYear) cards.push({ header: '5 Year', goal: g.fiveYear });
    if (cards.length === 0) {
      cards.push(
        { header: '1 Year', goal: '' },
        { header: '3 Year', goal: '' },
        { header: '5 Year', goal: '' }
      );
    }
    return cards;
  }
  return [];
}

/** SWOT columns rendered on the analysis (threats are stored but not shown, as before). */
const SWOT_COLUMNS = [
  { key: 'strengths', title: 'Strengths', tone: 'green', emoji: '💪', itemEmoji: '✅' },
  { key: 'weaknesses', title: 'Weaknesses', tone: 'red', emoji: '🚧', itemEmoji: '⚠️' },
  { key: 'opportunities', title: 'Opportunities', tone: 'blue', emoji: '💡', itemEmoji: '🚀' },
];

const AssessmentAnalysis = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isCrmContext = location.pathname.includes('/crm/');
  const { currentUser } = useAuth();
  const { darkMode } = useTheme();
  
  const isStartSection = location.pathname.includes('/start/');
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customGrowthPercentage, setCustomGrowthPercentage] = useState(null);
  const [isEditingGrowth, setIsEditingGrowth] = useState(false);
  const [isEditingSWOT, setIsEditingSWOT] = useState(false);
  const [editableSWOT, setEditableSWOT] = useState({
    strengths: [],
    weaknesses: [],
    opportunities: [],
    threats: []
  });
  const [editableLevers, setEditableLevers] = useState([]); // array of { emoji, header, description }
  const [isEditingLevers, setIsEditingLevers] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(null); // index when open
  const [emojiSearch, setEmojiSearch] = useState('');
  const [editableGoals, setEditableGoals] = useState([]); // array of { header, goal }
  const [isEditingGoals, setIsEditingGoals] = useState(false);
  const [editableImplementationResults, setEditableImplementationResults] = useState([]); // array of text strings
  const [isEditingImplementationResults, setIsEditingImplementationResults] = useState(false);
  const [programs, setPrograms] = useState({});
  const [billingCycles, setBillingCycles] = useState({});
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [programsLoading, setProgramsLoading] = useState(true);
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [selectedShowcaseFeature, setSelectedShowcaseFeature] = useState(null);
  const [selectedProgramForDetail, setSelectedProgramForDetail] = useState(null);
  const [selectedTierForDetail, setSelectedTierForDetail] = useState('');
  const [suggestedProgramKey, setSuggestedProgramKey] = useState(null);
  const [processStep, setProcessStep] = useState(0); // Our Process preview, driven by the 1-2-3 cards
  const [startStep, setStartStep] = useState(0); // Getting Started preview, driven by its 1-2-3 cards
  const [betweenStarsFilled, setBetweenStarsFilled] = useState({ 0: false, 1: false }); // stars between Lite-Plus and Plus-Pro
  const [assessmentStatus, setAssessmentStatus] = useState('in_review'); // 'in_review' | 'complete'
  const [savingStatus, setSavingStatus] = useState(false);

  // Complete = read-only for the analysis content (goals, SWOT, implementations, audit, growth rate).
  // The program section stays live so the suggestion can still be changed or reset.
  const isLocked = assessmentStatus === 'complete';
  useEffect(() => {
    if (!isLocked) return;
    setIsEditingGoals(false);
    setIsEditingSWOT(false);
    setIsEditingLevers(false);
    setIsEditingImplementationResults(false);
    setIsEditingGrowth(false);
    setShowEmojiPicker(null);
  }, [isLocked]);
  const programDetailsSectionRef = useRef(null);
  const effectiveLeadIdRef = useRef(null); // actual Firestore doc ID (may differ from leadId when email:xxx)

  useEffect(() => {
    const loadLeadData = async () => {
      if (!leadId) {
        setError('Lead ID is required');
        setLoading(false);
        return;
      }

      let actualLeadId = leadId;
      
      // If leadId starts with "email:", extract the email and find the account
      if (leadId.startsWith('email:')) {
        const email = leadId.replace('email:', '');
        try {
          const normalizedEmail = email.toLowerCase().trim();
          const searchQueries = [
            query(collection(db, 'accounts'), where('contact.email', '==', normalizedEmail), where('type', '==', 'Leads')),
            query(collection(db, 'accounts'), where('email', '==', normalizedEmail), where('type', '==', 'Leads')),
            query(collection(db, 'accounts'), where('contact.email', '==', normalizedEmail)),
            query(collection(db, 'accounts'), where('email', '==', normalizedEmail))
          ];
          
          let foundAccountId = null;
          for (const searchQuery of searchQueries) {
            try {
              const accountsSnapshot = await getDocs(searchQuery);
              if (!accountsSnapshot.empty) {
                foundAccountId = accountsSnapshot.docs[0].id;
                break;
              }
            } catch (err) {
              // Continue to next query
            }
          }
          
          if (foundAccountId) {
            actualLeadId = foundAccountId;
            // Update the URL to use the actual leadId
            window.history.replaceState(null, '', `/start/assessment-analysis/${foundAccountId}`);
          } else {
            setError('Account not found. Please ensure you have completed your assessment.');
            setLoading(false);
            return;
          }
        } catch (err) {
          console.error('Error finding account by email:', err);
          setError('Unable to find your account. Please contact support.');
          setLoading(false);
          return;
        }
      }

      effectiveLeadIdRef.current = actualLeadId;
      console.log('[AssessmentAnalysis] effectiveLeadIdRef set', { actualLeadId, leadId });

      // Set up real-time listener for lead data
      const leadDocRef = doc(db, 'accounts', actualLeadId);
      const unsubscribe = onSnapshot(leadDocRef, 
        (docSnapshot) => {
          try {
            if (!docSnapshot.exists()) {
              // If document doesn't exist, try to find by email as fallback
              if (currentUser?.email) {
                const findAccountByEmail = async () => {
                  try {
                    const normalizedEmail = currentUser.email.toLowerCase().trim();
                    const searchQueries = [
                      query(collection(db, 'accounts'), where('contact.email', '==', normalizedEmail), where('type', '==', 'Leads')),
                      query(collection(db, 'accounts'), where('email', '==', normalizedEmail), where('type', '==', 'Leads')),
                      query(collection(db, 'accounts'), where('contact.email', '==', normalizedEmail)),
                      query(collection(db, 'accounts'), where('email', '==', normalizedEmail))
                    ];
                    
                    for (const searchQuery of searchQueries) {
                      try {
                        const accountsSnapshot = await getDocs(searchQuery);
                        if (!accountsSnapshot.empty) {
                          const foundAccountId = accountsSnapshot.docs[0].id;
                          const foundAccountDoc = await getDoc(doc(db, 'accounts', foundAccountId));
                          if (foundAccountDoc.exists()) {
                            const data = foundAccountDoc.data();
                            setLead(data);
                            setLoading(false);
                            setError(null);
                            // Update URL
                            window.history.replaceState(null, '', `/start/assessment-analysis/${foundAccountId}`);
                            return;
                          }
                        }
                      } catch (err) {
                        // Continue
                      }
                    }
                  } catch (err) {
                    console.error('Error in fallback email search:', err);
                  }
                  setError('Lead not found');
                  setLoading(false);
                };
                findAccountByEmail();
                return;
              }
              throw new Error('Lead not found');
            }
            
            const data = docSnapshot.data();
            // Don't require type to be 'Leads' - allow any account type
            // if (data.type !== 'Leads') {
            //   throw new Error('This is not a lead');
            // }
            console.log('[AssessmentAnalysis] Lead loaded from Firestore', { docId: docSnapshot.id, hasProposedProgram: !!data?.proposedProgram, proposedProgramKeys: data?.proposedProgram ? Object.keys(data.proposedProgram) : [] });
            
            setLead(data);
            setLoading(false);
            setError(null);
          } catch (err) {
            console.error('Error loading lead data:', err);
            setError(err.message);
            setLoading(false);
          }
        },
        (err) => {
          console.error('Error in onSnapshot:', err);
          setError('Failed to load lead data');
          setLoading(false);
        }
      );

      return () => unsubscribe();
    };

    loadLeadData();
  }, [leadId, currentUser]);

  // Sync assessment status from lead
  useEffect(() => {
    if (lead?.assessmentStatus === 'complete' || lead?.assessmentStatus === 'in_review') {
      setAssessmentStatus(lead.assessmentStatus);
    } else {
      setAssessmentStatus('in_review');
    }
  }, [lead?.assessmentStatus]);

  // Get sales data with fallbacks (must be defined before calculations)
  // Merge salesData and businessAssessment to get all available data
  const salesData = {
    ...(lead?.businessAssessment || {}),
    ...(lead?.salesData || {})
  };

  // Calculate BBA Rating based on sales metrics
  const calculateBBARating = useMemo(() => {
    if (!salesData || Object.keys(salesData).length === 0) return 0;
    
    let score = 0;
    let factors = 0;
    
    // Sales Rating (1-5, contributes to overall)
    if (salesData.salesRating) {
      score += salesData.salesRating;
      factors += 1;
    }
    
    // Close Ratio (normalize to 1-5 scale: <30% = 1-2, 30-50% = 3-4, 50%+ = 5)
    if (salesData.closeRatio !== undefined && salesData.closeRatio !== null) {
      if (salesData.closeRatio < 30) {
        score += 2;
      } else if (salesData.closeRatio < 50) {
        score += 4;
      } else {
        score += 5;
      }
      factors += 1;
    }
    
    // Referral Business % (higher is better, normalize)
    if (salesData.referralBusinessPercentage !== undefined && salesData.referralBusinessPercentage !== null) {
      const referralScore = Math.min(5, Math.max(1, Math.round(salesData.referralBusinessPercentage / 20)));
      score += referralScore;
      factors += 1;
    }
    
    // Repeat Customer % (compare to average 2-8%)
    if (salesData.repeatCustomerPercentage !== undefined && salesData.repeatCustomerPercentage !== null) {
      const repeatScore = salesData.repeatCustomerPercentage >= 2 && salesData.repeatCustomerPercentage <= 8 
        ? 5 
        : salesData.repeatCustomerPercentage < 2 
          ? 2 
          : 3;
      score += repeatScore;
      factors += 1;
    }
    
    return factors > 0 ? Math.round(score / factors) : 0;
  }, [salesData]);
  
  // Owner Rating (from salesRating)
  const ownerRating = salesData.salesRating || 0;
  
  // BBA Rating (calculated)
  const bbaRating = calculateBBARating;

  // Get operations data with fallbacks (must be defined before calculations)
  // Merge operationsData and businessAssessment to get all available data
  const operationsData = {
    ...(lead?.businessAssessment || {}),
    ...(lead?.operationsData || {})
  };
  
  // Calculate BBA Rating for Operations based on operations metrics
  const calculateOperationsBBARating = useMemo(() => {
    if (!operationsData || Object.keys(operationsData).length === 0) return 0;
    
    let score = 0;
    let factors = 0;
    
    // Operations Rating (1-5, contributes to overall)
    if (operationsData.operationsRating) {
      score += operationsData.operationsRating;
      factors += 1;
    }
    
    // SOPs (yes = 5, no = 1)
    if (operationsData.hasSOPs !== undefined && operationsData.hasSOPs !== null) {
      score += operationsData.hasSOPs === 'yes' ? 5 : 1;
      factors += 1;
    }
    
    // Job Descriptions (yes = 5, no = 1)
    if (operationsData.hasJobDescriptions !== undefined && operationsData.hasJobDescriptions !== null) {
      score += operationsData.hasJobDescriptions === 'yes' ? 5 : 1;
      factors += 1;
    }
    
    // KPIs (yes = 5, no = 1)
    if (operationsData.hasKPIs !== undefined && operationsData.hasKPIs !== null) {
      score += operationsData.hasKPIs === 'yes' ? 5 : 1;
      factors += 1;
    }
    
    // Company Culture Rating (1-5, contributes directly)
    if (operationsData.companyCultureRating && operationsData.companyCultureRating > 0) {
      score += operationsData.companyCultureRating;
      factors += 1;
    }
    
    // Team Grade (A = 5, B = 4, C = 3, D = 2, F = 1)
    if (operationsData.teamGrade) {
      const gradeScores = { 'A': 5, 'B': 4, 'C': 3, 'D': 2, 'F': 1 };
      score += gradeScores[operationsData.teamGrade.toUpperCase()] || 3;
      factors += 1;
    }
    
    return factors > 0 ? Math.round(score / factors) : 0;
  }, [operationsData]);
  
  // Operations Owner Rating (from operationsRating)
  const operationsOwnerRating = operationsData.operationsRating || 0;
  
  // Operations BBA Rating (calculated)
  const operationsBBARating = calculateOperationsBBARating;

  // Get finance data with fallbacks (must be defined before calculations)
  // Merge financeData and businessAssessment to get all available data
  const financeData = {
    ...(lead?.businessAssessment || {}),
    ...(lead?.financeData || {})
  };
  
  // Calculate BBA Rating for Finance based on finance metrics
  const calculateFinanceBBARating = useMemo(() => {
    if (!financeData || Object.keys(financeData).length === 0) return 0;
    
    let score = 0;
    let factors = 0;
    
    // Finance Rating (1-5, contributes to overall)
    if (financeData.financeRating) {
      score += financeData.financeRating;
      factors += 1;
    }
    
    // Monthly Reconciliation (yes = 5, no = 1)
    if (financeData.hasMonthlyReconciliation !== undefined && financeData.hasMonthlyReconciliation !== null) {
      score += financeData.hasMonthlyReconciliation === 'yes' ? 5 : 1;
      factors += 1;
    }
    
    // Cash Flow Projection (yes = 5, no = 1)
    if (financeData.hasCashFlowProjection !== undefined && financeData.hasCashFlowProjection !== null) {
      score += financeData.hasCashFlowProjection === 'yes' ? 5 : 1;
      factors += 1;
    }
    
    // Project Budget vs Actuals (yes = 5, no = 1)
    if (financeData.hasProjectBudgetsVsActuals !== undefined && financeData.hasProjectBudgetsVsActuals !== null) {
      score += financeData.hasProjectBudgetsVsActuals === 'yes' ? 5 : 1;
      factors += 1;
    }
    
    // Debt (yes = 1, no = 5) - inverted because debt is bad
    if (financeData.hasDebt !== undefined && financeData.hasDebt !== null) {
      score += financeData.hasDebt === 'yes' ? 1 : 5;
      factors += 1;
    }
    
    // Financial Partners (yes = 5, no = 1)
    if (financeData.hasFinancialPartners !== undefined && financeData.hasFinancialPartners !== null) {
      score += financeData.hasFinancialPartners === 'yes' ? 5 : 1;
      factors += 1;
    }
    
    // Credit Line (yes = 5, no = 1)
    if (financeData.hasCreditLine !== undefined && financeData.hasCreditLine !== null) {
      score += financeData.hasCreditLine === 'yes' ? 5 : 1;
      factors += 1;
    }
    
    // Unencumbered Cash as % of revenue (under 5% = 1, 5-10% = 3, over 10% = 5)
    const unencumberedCash = parseFloat(financeData.unencumberedCash || 0);
    const currentYearRevenue = parseFloat(salesData.currentYearProjection || salesData.revenueData?.currentYearProjection || 0);
    if (unencumberedCash > 0 && currentYearRevenue > 0) {
      const cashPercentage = (unencumberedCash / currentYearRevenue) * 100;
      if (cashPercentage < 5) {
        score += 1;
      } else if (cashPercentage <= 10) {
        score += 3;
      } else {
        score += 5;
      }
      factors += 1;
    }
    
    // Gross Profit Margin (under 30% = 1, 30-40% = 3, over 40% = 5)
    const gpm = parseFloat(financeData.grossProfitMargin || 0);
    if (gpm > 0) {
      if (gpm < 30) {
        score += 1;
      } else if (gpm <= 40) {
        score += 3;
      } else {
        score += 5;
      }
      factors += 1;
    }
    
    // Net Profit Margin (under 5% = 1, 5-10% = 3, over 10% = 5)
    const npm = parseFloat(financeData.netProfitMargin || 0);
    if (npm > 0) {
      if (npm < 5) {
        score += 1;
      } else if (npm <= 10) {
        score += 3;
      } else {
        score += 5;
      }
      factors += 1;
    }
    
    return factors > 0 ? Math.round(score / factors) : 0;
  }, [financeData, salesData]);
  
  // Finance Owner Rating (from financeRating)
  const financeOwnerRating = financeData.financeRating || financeData.rating || 0;
  
  // Finance BBA Rating (calculated)
  const financeBBARating = calculateFinanceBBARating;

  // Get marketing data with fallbacks (must be defined before calculations)
  // Merge marketingData and businessAssessment to get all available data
  const marketingData = {
    ...(lead?.businessAssessment || {}),
    ...(lead?.marketingData || {})
  };
  
  // Calculate BBA Rating for Marketing based on marketing metrics
  const calculateMarketingBBARating = useMemo(() => {
    if (!marketingData || Object.keys(marketingData).length === 0) return 0;
    
    let score = 0;
    let factors = 0;
    
    // Marketing Rating (1-5, contributes to overall)
    if (marketingData.marketingRating || marketingData.rating) {
      score += marketingData.marketingRating || marketingData.rating;
      factors += 1;
    }
    
    // Marketing Budget (having a budget is good)
    if (marketingData.monthlyMarketingBudget || marketingData.annualMarketingBudget) {
      score += 4; // Having a budget is positive
      factors += 1;
    }
    
    // Running Digital Ads (yes = 5, no = 1)
    if (marketingData.runningDigitalAds !== undefined && marketingData.runningDigitalAds !== null) {
      score += marketingData.runningDigitalAds === 'yes' ? 5 : 1;
      factors += 1;
    }
    
    // Digital Ad Platforms (having platforms is good)
    if (marketingData.digitalAdPlatforms && Array.isArray(marketingData.digitalAdPlatforms) && marketingData.digitalAdPlatforms.length > 0) {
      // More platforms = better (up to 5)
      const platformScore = Math.min(5, marketingData.digitalAdPlatforms.length);
      score += platformScore;
      factors += 1;
    }
    
    // Website Management (having a manager is good)
    if (marketingData.websiteManager) {
      score += 4; // Having website management is positive
      factors += 1;
    }
    
    return factors > 0 ? Math.round(score / factors) : 0;
  }, [marketingData]);
  
  // Marketing Owner Rating (from marketingRating)
  const marketingOwnerRating = marketingData.marketingRating || marketingData.rating || 0;
  
  // Marketing BBA Rating (calculated)
  const marketingBBARating = calculateMarketingBBARating;

  // Get tech data with fallbacks (must be defined before calculations)
  // Merge techData and businessAssessment to get all available data
  const techData = {
    ...(lead?.businessAssessment || {}),
    ...(lead?.techData || {})
  };
  
  // Calculate BBA Rating for Tech based on tech metrics
  const calculateTechBBARating = useMemo(() => {
    if (!techData || Object.keys(techData).length === 0) return 0;
    
    let score = 0;
    let factors = 0;
    
    // Tech Rating (1-5, contributes to overall)
    if (techData.techRating || techData.rating) {
      score += techData.techRating || techData.rating;
      factors += 1;
    }
    
    // CRM Rating (JobTread/Buildertrend = 5, Procore/HubSpot/Other = 3, None = 1)
    const crm = techData.whatCRM || '';
    if (crm) {
      const crmLower = crm.toLowerCase();
      if (crmLower.includes('jobtread') || crmLower.includes('buildertrend')) {
        score += 5; // Great
      } else if (crmLower.includes('procore') || crmLower.includes('hubspot') || crmLower === 'other') {
        score += 3; // Good
      } else if (crmLower === 'none' || crmLower === '') {
        score += 1; // Poor
      } else {
        score += 3; // Default to good for other CRMs
      }
      factors += 1;
    }
    
    // CRM Optimization (under 50% = 1, 50-75% = 3, over 75% = 5)
    const crmOpt = parseFloat(techData.crmOptimization || 0);
    if (crmOpt > 0) {
      if (crmOpt < 50) {
        score += 1; // Poor
      } else if (crmOpt <= 75) {
        score += 3; // Good
      } else {
        score += 5; // Great
      }
      factors += 1;
    }
    
    // Automations (yes = 5, no = 1)
    if (techData.hasAutomations !== undefined && techData.hasAutomations !== null) {
      score += techData.hasAutomations === 'yes' ? 5 : 1;
      factors += 1;
    }
    
    // AI Tools (yes = 5, no = 1)
    if (techData.hasAITools !== undefined && techData.hasAITools !== null) {
      score += techData.hasAITools === 'yes' ? 5 : 1;
      factors += 1;
    }
    
    // Website Plugins (yes = 5, no = 1)
    const hasPlugins = techData.hasWebsitePlugins || techData.hasCustomWebsitePlugins;
    if (hasPlugins !== undefined && hasPlugins !== null) {
      score += hasPlugins === 'yes' ? 5 : 1;
      factors += 1;
    }
    
    return factors > 0 ? Math.round(score / factors) : 0;
  }, [techData]);
  
  // Tech Owner Rating (from techRating)
  const techOwnerRating = techData.techRating || techData.rating || 0;
  
  // Tech BBA Rating (calculated)
  const techBBARating = calculateTechBBARating;

  // Calculate overall company ratings and determine strongest/weakest departments
  const companyAnalysis = useMemo(() => {
    const departments = [
      { name: 'Sales', ownerRating, bbaRating },
      { name: 'Operations', ownerRating: operationsOwnerRating, bbaRating: operationsBBARating },
      { name: 'Finance', ownerRating: financeOwnerRating, bbaRating: financeBBARating },
      { name: 'Marketing', ownerRating: marketingOwnerRating, bbaRating: marketingBBARating },
      { name: 'Technology', ownerRating: techOwnerRating, bbaRating: techBBARating }
    ];

    // Calculate overall ratings (average of all departments)
    const validOwnerRatings = departments.filter(d => d.ownerRating > 0).map(d => d.ownerRating);
    const validBBARatings = departments.filter(d => d.bbaRating > 0).map(d => d.bbaRating);
    
    const overallOwnerRating = validOwnerRatings.length > 0
      ? Math.round(validOwnerRatings.reduce((a, b) => a + b, 0) / validOwnerRatings.length)
      : 0;
    
    const overallBBARating = validBBARatings.length > 0
      ? Math.round(validBBARatings.reduce((a, b) => a + b, 0) / validBBARatings.length)
      : 0;

    // Find strongest and weakest departments (by BBA rating)
    const departmentsWithRatings = departments.filter(d => d.bbaRating > 0);
    const strongest = departmentsWithRatings.length > 0
      ? departmentsWithRatings.reduce((a, b) => a.bbaRating > b.bbaRating ? a : b)
      : null;
    const weakest = departmentsWithRatings.length > 0
      ? departmentsWithRatings.reduce((a, b) => a.bbaRating < b.bbaRating ? a : b)
      : null;

    return {
      overallOwnerRating,
      overallBBARating,
      strongest: strongest?.name || 'N/A',
      weakest: weakest?.name || 'N/A',
      departments
    };
  }, [ownerRating, bbaRating, operationsOwnerRating, operationsBBARating, financeOwnerRating, financeBBARating, marketingOwnerRating, marketingBBARating, techOwnerRating, techBBARating]);

  // Calculate SWOT data based on lead assessment data
  const swotData = useMemo(() => {
    const strengths = [];
    const weaknesses = [];
    const opportunities = [];

    // Helper function to check if value is "no" (handles empty strings, null, undefined, false, and 'no')
    const isNo = (value) => {
      if (value === null || value === undefined || value === '') return true;
      if (value === 'no' || value === false) return true;
      return false;
    };

    // Helper function to check if value is "yes" (handles true and 'yes')
    const isYes = (value) => {
      return value === 'yes' || value === true;
    };

    // Operations Data
    if (operationsData.hasSOPs !== undefined && operationsData.hasSOPs !== null && operationsData.hasSOPs !== '') {
      if (isYes(operationsData.hasSOPs)) {
        strengths.push('Has Standard Operating Procedures (SOPs)');
      } else if (isNo(operationsData.hasSOPs)) {
        opportunities.push('Implement Standard Operating Procedures (SOPs)');
      }
    }

    if (operationsData.hasJobDescriptions !== undefined && operationsData.hasJobDescriptions !== null && operationsData.hasJobDescriptions !== '') {
      if (isYes(operationsData.hasJobDescriptions)) {
        strengths.push('Has Job Descriptions');
      } else if (isNo(operationsData.hasJobDescriptions)) {
        opportunities.push('Create Job Descriptions');
      }
    } else if (operationsData.hasJobDescriptions === '') {
      // Empty string means "no"
      opportunities.push('Create Job Descriptions');
    }

    if (operationsData.hasKPIs !== undefined && operationsData.hasKPIs !== null && operationsData.hasKPIs !== '') {
      if (isYes(operationsData.hasKPIs)) {
        strengths.push('Has Key Performance Indicators (KPIs)');
      } else if (isNo(operationsData.hasKPIs)) {
        opportunities.push('Implement Key Performance Indicators (KPIs)');
      }
    } else if (operationsData.hasKPIs === '') {
      // Empty string means "no"
      opportunities.push('Implement Key Performance Indicators (KPIs)');
    }

    if (operationsData.companyCultureRating >= 4) {
      strengths.push('Strong Company Culture');
    } else if (operationsData.companyCultureRating <= 2 && operationsData.companyCultureRating > 0) {
      weaknesses.push('Weak Company Culture');
    }

    if (operationsData.teamGrade && ['A', 'B'].includes(operationsData.teamGrade.toUpperCase())) {
      strengths.push(`Strong Team (Grade ${operationsData.teamGrade.toUpperCase()})`);
    } else if (operationsData.teamGrade && ['D', 'F'].includes(operationsData.teamGrade.toUpperCase())) {
      weaknesses.push(`Weak Team (Grade ${operationsData.teamGrade.toUpperCase()})`);
    }

    // Sales Data - Repeat Customers
    const repeatCustomerPercentage = parseFloat(salesData.repeatCustomerPercentage || 0);
    if (repeatCustomerPercentage >= 10) {
      strengths.push('Strong Repeat Customer Base');
    } else if (repeatCustomerPercentage > 0 && repeatCustomerPercentage < 5) {
      weaknesses.push('Low Repeat Customer Percentage');
    }

    // Finance Data
    if (isYes(financeData.hasMonthlyReconciliation)) {
      strengths.push('Performs Monthly Reconciliation');
    } else if (isNo(financeData.hasMonthlyReconciliation)) {
      opportunities.push('Implement Monthly Reconciliation');
    }

    if (isYes(financeData.hasCashFlowProjection)) {
      strengths.push('Has Cash Flow Projections');
    } else if (isNo(financeData.hasCashFlowProjection)) {
      opportunities.push('Implement Cash Flow Projections');
    }

    if (isYes(financeData.hasProjectBudgetsVsActuals)) {
      strengths.push('Tracks Project Budget vs Actuals');
    } else if (isNo(financeData.hasProjectBudgetsVsActuals)) {
      opportunities.push('Implement Project Budget vs Actuals Tracking');
    }

    if (isYes(financeData.hasCreditLine)) {
      strengths.push('Has Credit Line');
    } else if (isNo(financeData.hasCreditLine)) {
      opportunities.push('Establish Credit Line');
    }

    if (isYes(financeData.hasDebt)) {
      weaknesses.push('Has Outstanding Debt');
    }

    if (isYes(financeData.hasFinancialPartners)) {
      strengths.push('Has Financial Partners');
    }

    // Unencumbered Cash rating
    if (financeData.unencumberedCash) {
      const unencumberedCash = parseFloat(financeData.unencumberedCash || 0);
      const currentYearRevenue = parseFloat(salesData.currentYearProjection || salesData.revenueData?.currentYearProjection || 0);
      if (currentYearRevenue > 0) {
        const cashPercentage = (unencumberedCash / currentYearRevenue) * 100;
        if (cashPercentage > 10) {
          strengths.push('Strong Unencumbered Cash Position');
        }
      } else if (unencumberedCash > 0) {
        // If revenue is 0 or missing, just check if they have cash
        strengths.push('Has Unencumbered Cash');
      }
    }

    // Marketing Data
    if (isYes(marketingData.runningDigitalAds)) {
      strengths.push('Running Digital Advertising');
    } else if (isNo(marketingData.runningDigitalAds)) {
      opportunities.push('Implement Digital Advertising');
    }

    // Tech Data
    const crm = techData.whatCRM || '';
    const crmLower = crm.toLowerCase();
    if (crmLower === 'none' || crmLower === '') {
      opportunities.push('Implement or Upgrade CRM System');
    } else if (crmLower.includes('jobtread') || crmLower.includes('buildertrend')) {
      strengths.push('Strong CRM System (JobTread/Buildertrend)');
    }

    const crmOpt = parseFloat(techData.crmOptimization || 0);
    if (crmOpt > 75) {
      strengths.push('Strong CRM Optimization');
    } else if (crmOpt > 0 && crmOpt < 50) {
      weaknesses.push('Poor CRM Optimization');
    }

    if (isYes(techData.hasAutomations)) {
      strengths.push('Has Business Automations');
    } else if (isNo(techData.hasAutomations)) {
      opportunities.push('Implement Business Automations');
    }

    if (isYes(techData.hasAITools)) {
      strengths.push('Uses AI Tools');
    } else if (isNo(techData.hasAITools)) {
      opportunities.push('Implement AI Tools');
    }

    const hasPlugins = techData.hasWebsitePlugins || techData.hasCustomWebsitePlugins;
    if (isYes(hasPlugins)) {
      strengths.push('Has Website Plugins');
    } else if (isNo(hasPlugins)) {
      opportunities.push('Implement Website Plugins');
    }

    return {
      strengths: strengths,
      weaknesses: weaknesses,
      opportunities: opportunities,
      threats: []
    };
  }, [operationsData, financeData, marketingData, techData, salesData]);

  // Initialize editable SWOT data from calculated SWOT and any saved custom data
  useEffect(() => {
    if (lead && !isEditingSWOT) { // Don't overwrite if user is currently editing
      const customSWOT = lead.customSWOT || {};
      let newSWOT;
      // If custom SWOT exists, use it; otherwise use calculated SWOT
      if (customSWOT.strengths || customSWOT.weaknesses || customSWOT.opportunities || customSWOT.threats) {
        newSWOT = {
          strengths: customSWOT.strengths || [],
          weaknesses: customSWOT.weaknesses || [],
          opportunities: customSWOT.opportunities || [],
          threats: customSWOT.threats || []
        };
      } else if (swotData && (swotData.strengths?.length > 0 || swotData.weaknesses?.length > 0 || swotData.opportunities?.length > 0 || swotData.threats?.length > 0)) {
        newSWOT = {
          strengths: swotData.strengths || [],
          weaknesses: swotData.weaknesses || [],
          opportunities: swotData.opportunities || [],
          threats: swotData.threats || []
        };
      }
      if (newSWOT) {
        setEditableSWOT((prev) => {
          const prevStr = JSON.stringify(prev);
          const newStr = JSON.stringify(newSWOT);
          if (prevStr === newStr) return prev;
          return newSWOT;
        });
      }
    }
  }, [leadId, lead?.customSWOT, swotData, isEditingSWOT]); // Include swotData for initial load, but check isEditingSWOT to prevent overwriting edits

  // Initialize editable levers from lead data (support array, legacy object, or defaults when unset)
  useEffect(() => {
    if (!lead || isEditingLevers) return;
    const newLevers = parseImmediateLevers(lead.immediateLevers);
    setEditableLevers((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(newLevers)) return prev;
      return newLevers;
    });
  }, [lead?.id, lead?.immediateLevers, isEditingLevers]);

  // Initialize editable goals from lead data (array, legacy { oneYear, threeYear, fiveYear }, or defaults when unset)
  useEffect(() => {
    if (!lead || isEditingGoals) return;
    const newGoals = parseGoals(lead.goals);
    setEditableGoals((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(newGoals)) return prev;
      return newGoals;
    });
  }, [lead?.id, lead?.goals, isEditingGoals]);

  // Initialize implementation results from lead data (defaults when unset)
  useEffect(() => {
    if (!lead || isEditingImplementationResults) return;
    const newResults = parseImplementationResults(lead.implementationResults);
    setEditableImplementationResults((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(newResults)) return prev;
      return newResults;
    });
  }, [lead?.id, lead?.implementationResults, isEditingImplementationResults]);

  // Initialize suggested program from lead data
  useEffect(() => {
    if (lead?.suggestedProgram) {
      setSuggestedProgramKey(lead.suggestedProgram);
    }
  }, [lead?.suggestedProgram]);

  // When lead has proposedProgram, show Program Details section with that data
  useEffect(() => {
    if (lead?.proposedProgram && !selectedProgramForDetail) {
      const pp = lead.proposedProgram;
      setSelectedProgramForDetail({
        name: pp.programName?.replace(/\s*Program\s*$/, '') || 'Program',
        features: pp.features || {},
        pricePerMonth: pp.totalPricePerMonth ?? 0,
        minimumTerm: pp.programMinimumTerm ?? 12
      });
      setSelectedTierForDetail(pp.selectedTier || '');
    }
  }, [lead?.proposedProgram]);

  // Load programs, billing cycles, alaCarteItems for Suggested Program section
  useEffect(() => {
    const loadProgramsData = async () => {
      try {
        setProgramsLoading(true);
        const programsSnapshot = await getDocs(collection(db, 'programs'));
        if (!programsSnapshot.empty) {
          const programsData = {};
          programsSnapshot.forEach((docSnap) => {
            const programData = docSnap.data();
            if (programData && typeof programData === 'object') {
              if (!programData.features || typeof programData.features !== 'object') programData.features = {};
              if (!Array.isArray(programData.showcaseFeatures)) {
                programData.showcaseFeatures = Object.entries(programData.features || {}).filter(([, fd]) => fd?.showcase).map(([name]) => ({ text: name, showcase: true, description: '' }));
              } else {
                programData.showcaseFeatures = (programData.showcaseFeatures || []).map((item) =>
                  typeof item === 'string' ? { text: item, showcase: true, description: '' } : { ...item, description: item.description || '' }
                );
              }
              if (!programData.name) programData.name = docSnap.id;
              if (!programData.description) programData.description = 'Program description';
              if (!programData.pricePerMonth) programData.pricePerMonth = 0;
              if (typeof programData.isMostPopular !== 'boolean') programData.isMostPopular = false;
              programsData[docSnap.id] = programData;
            }
          });
          setPrograms(programsData);
        }
        const billingSnapshot = await getDocs(collection(db, 'billingCycles'));
        if (!billingSnapshot.empty) {
          const billingData = {};
          billingSnapshot.forEach((docSnap) => { billingData[docSnap.id] = docSnap.data(); });
          setBillingCycles(billingData);
          if (Object.keys(billingData).length > 0 && !billingCycle) setBillingCycle(Object.keys(billingData)[0]);
        }
        const itemsSnapshot = await getDocs(collection(db, 'alaCarteItems'));
        setAvailableItems(itemsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error loading programs:', err);
      } finally {
        setProgramsLoading(false);
      }
    };
    loadProgramsData();
  }, []);

  // Save SWOT data to Firestore
  const saveSWOTData = async () => {
    if (!leadId || !currentUser) return;
    
    try {
      const leadDocRef = doc(db, 'accounts', leadId);
      await updateDoc(leadDocRef, {
        customSWOT: editableSWOT,
        lastUpdated: new Date().toISOString()
      });
      setIsEditingSWOT(false);
    } catch (error) {
      console.error('Error saving SWOT data:', error);
      alert('Error saving SWOT data. Please try again.');
    }
  };

  // Save Immediate Levers data to Firestore
  const saveLeversData = async () => {
    if (!leadId || !currentUser) return;
    
    try {
      const leadDocRef = doc(db, 'accounts', leadId);
      await updateDoc(leadDocRef, {
        immediateLevers: editableLevers.map(c => ({ emoji: c.emoji || '', header: c.header || '', description: c.description || '', department: c.department || '' })),
        lastUpdated: new Date().toISOString()
      });
      setIsEditingLevers(false);
      setShowEmojiPicker(null);
      setEmojiSearch('');
    } catch (error) {
      console.error('Error saving Key Implementations:', error);
      alert('Error saving Key Implementations. Please try again.');
    }
  };

  // Save Goals data to Firestore
  const saveGoalsData = async () => {
    if (!leadId || !currentUser) return;
    try {
      const leadDocRef = doc(db, 'accounts', leadId);
      await updateDoc(leadDocRef, {
        goals: editableGoals.map(c => ({ header: c.header || '', goal: c.goal || '' })),
        lastUpdated: new Date().toISOString()
      });
      setIsEditingGoals(false);
    } catch (error) {
      console.error('Error saving Goals:', error);
      alert('Error saving Goals. Please try again.');
    }
  };

  const saveAssessmentStatus = async (newStatus) => {
    const docId = effectiveLeadIdRef.current || leadId;
    if (!docId || String(docId).startsWith('email:')) return;
    setSavingStatus(true);
    try {
      await updateAccount(docId, { assessmentStatus: newStatus });
      setAssessmentStatus(newStatus);
    } catch (error) {
      console.error('Error saving assessment status:', error);
      alert('Error saving status. Please try again.');
    } finally {
      setSavingStatus(false);
    }
  };

  const addImplementationResultRow = () => {
    setEditableImplementationResults(prev => [...prev, '']);
  };
  const removeImplementationResultRow = (index) => {
    setEditableImplementationResults(prev => prev.filter((_, i) => i !== index));
  };
  const updateImplementationResultRow = (index, value) => {
    setEditableImplementationResults(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };
  const moveImplementationResultUp = (index) => {
    if (index === 0) return;
    setEditableImplementationResults(prev => {
      const newArray = [...prev];
      [newArray[index - 1], newArray[index]] = [newArray[index], newArray[index - 1]];
      return newArray;
    });
  };
  const moveImplementationResultDown = (index) => {
    setEditableImplementationResults(prev => {
      if (index >= prev.length - 1) return prev;
      const newArray = [...prev];
      [newArray[index], newArray[index + 1]] = [newArray[index + 1], newArray[index]];
      return newArray;
    });
  };
  const saveImplementationResultsData = async () => {
    if (!leadId || !currentUser) return;
    try {
      const leadDocRef = doc(db, 'accounts', leadId);
      await updateDoc(leadDocRef, {
        implementationResults: editableImplementationResults,
        lastUpdated: new Date().toISOString()
      });
      setIsEditingImplementationResults(false);
    } catch (error) {
      console.error('Error saving Implementation Results:', error);
      alert('Error saving Implementation Results. Please try again.');
    }
  };

  const setSuggestedProgram = async (programKey) => {
    const newKey = suggestedProgramKey === programKey ? null : programKey;
    setSuggestedProgramKey(newKey);
    const docId = effectiveLeadIdRef.current || leadId;
    if (docId) {
      try {
        const leadDocRef = doc(db, 'accounts', docId);
        await updateDoc(leadDocRef, {
          suggestedProgram: newKey,
          lastUpdated: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error saving suggested program:', error);
      }
    }
  };

  const addGoalCard = () => {
    setEditableGoals(prev => [...prev, { header: '', goal: '' }]);
  };
  const removeGoalCard = (index) => {
    setEditableGoals(prev => prev.filter((_, i) => i !== index));
  };
  const moveGoalCardUp = (index) => {
    if (index === 0) return;
    setEditableGoals(prev => {
      const newArray = [...prev];
      [newArray[index - 1], newArray[index]] = [newArray[index], newArray[index - 1]];
      return newArray;
    });
  };
  const moveGoalCardDown = (index) => {
    setEditableGoals(prev => {
      if (index >= prev.length - 1) return prev;
      const newArray = [...prev];
      [newArray[index], newArray[index + 1]] = [newArray[index + 1], newArray[index]];
      return newArray;
    });
  };
  const updateGoalCard = (index, field, value) => {
    setEditableGoals(prev => {
      const next = [...prev];
      if (!next[index]) next[index] = { header: '', goal: '' };
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const updateLeverCard = (index, field, value) => {
    setEditableLevers(prev => {
      const next = [...prev];
      if (!next[index]) next[index] = { emoji: '', header: '', description: '', department: '' };
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addLeverCard = () => {
    setEditableLevers(prev => [...prev, { emoji: '', header: '', description: '', department: '' }]);
  };

  const LEVER_DEPARTMENTS = [
    { id: 'sales', name: 'Sales', color: 'red' },
    { id: 'operations', name: 'Operations', color: 'blue' },
    { id: 'finance', name: 'Finance', color: 'green' },
    { id: 'hr', name: 'HR', color: 'yellow' },
    { id: 'technology', name: 'Technology', color: 'purple' },
    { id: 'marketing', name: 'Marketing', color: 'orange' }
  ];
  const departmentPillClasses = {
    red: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-700',
    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-700',
    green: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-700',
    yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700',
    purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-700',
    orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200 dark:border-orange-700'
  };

  const removeLeverCard = (index) => {
    setEditableLevers(prev => prev.filter((_, i) => i !== index));
    if (showEmojiPicker === index) setShowEmojiPicker(null);
  };

  const moveLeverCardUp = (index) => {
    if (index === 0) return;
    setEditableLevers(prev => {
      const newArray = [...prev];
      [newArray[index - 1], newArray[index]] = [newArray[index], newArray[index - 1]];
      return newArray;
    });
    if (showEmojiPicker === index) setShowEmojiPicker(index - 1);
    else if (showEmojiPicker === index - 1) setShowEmojiPicker(index);
  };

  const moveLeverCardDown = (index) => {
    setEditableLevers(prev => {
      if (index >= prev.length - 1) return prev;
      const newArray = [...prev];
      [newArray[index], newArray[index + 1]] = [newArray[index + 1], newArray[index]];
      return newArray;
    });
    if (showEmojiPicker === index) setShowEmojiPicker(index + 1);
    else if (showEmojiPicker === index + 1) setShowEmojiPicker(index);
  };

  // Popular emojis (same as RoadmapTemplateOverview)
  const popularEmojis = [
    '🚀', '⚡', '🔥', '💡', '🎯', '📈', '🔄', '⚙️', '🎨', '📱',
    '💻', '🌐', '📊', '🎪', '🏆', '💎', '🌟', '🎭', '📝', '📋',
    '📅', '⏰', '🔔', '📧', '💬', '📞', '📹', '🎵', '🛒', '💰',
    '💳', '📦', '🚚', '🏢', '👥', '👤', '🎓', '🔐', '🔍', '📌',
    '📍', '🤖', '🧠', '🔧', '🔨', '🛠️', '✅', '❌', '⚠️', 'ℹ️',
    '📚', '📖', '✏️', '📐', '🧮', '🔬', '🧪', '🔭', '📡', '🛰️',
    '🎡', '🎢', '🎠', '🎬', '🎤', '🎧', '🎼', '🎹', '🥁', '🎷',
    '🎺', '🎸', '🎻', '🎲', '🎯', '🎳', '🎮', '🎰', '🧩', '📷',
    '📸', '📹', '📺', '📻', '📟', '📠', '☎️', '💡', '🔦', '🕯️',
    '🪔', '🧯', '💸', '💵', '🪙', '⚖️', '🪜', '🦯', '🦽', '🦼',
    '📑', '📄', '📃', '📜', '📰', '🗞️', '📓', '📔', '📒', '📕',
    '📗', '📘', '📙', '📚', '📖', '🔖', '🏷️', '💰', '💴', '💵',
    '💶', '💷', '💸', '💳', '🧾', '💹', '📊', '📈', '📉', '📇',
    '📋', '📁', '📂', '🗂️', '🗄️', '🗃️', '📅', '📆', '🗓️', '📆',
    '💌', '📧', '📨', '📩', '📤', '📥', '📦', '📫', '📪', '📬',
    '📭', '📮', '🗳️', '✉️', '💬', '💭', '🗯️', '🗨️', '👁️', '🗣️',
    '⌚', '📱', '📲', '💻', '⌨️', '🖥️', '🖨️', '🖱️', '🖲️', '🕹️',
    '🗜️', '💾', '💿', '📀', '📼', '🎙️', '🎚️', '🎛️', '⏱️', '⏲️',
    '🔋', '🔌', '🛢️', '🔧', '🔨', '⚒️', '🛠️', '⛏️', '🔩', '🧰',
    '🧲', '🔫', '💣', '🧨', '🔪', '🗡️', '⚔️', '🛡️', '🔮', '📿',
    '🍎', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🍈', '🍒', '🍑',
    '🥭', '🍍', '🥥', '🥝', '🍅', '🍆', '🥑', '🥦', '🥬', '🥒',
    '⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱',
    '✈️', '🛫', '🛬', '🛩️', '💺', '🚁', '🛸', '🚀', '🛰️', '🚂',
    '🚃', '🚄', '🚅', '🚆', '🚇', '🚈', '🚉', '🚊', '🚝', '🚞',
    '🚟', '🚠', '🚡', '🚋', '🚌', '🚍', '🚎', '🚐', '🚑', '🚒',
    '🚓', '🚔', '🚕', '🚖', '🚗', '🚘', '🚙', '🚚', '🚛', '🚜',
    '🏎️', '🏍️', '🛵', '🦽', '🦼', '🛴', '🚲', '🛺', '🛹', '🛼',
    '🗽', '🗼', '🏰', '🏯', '🏟️', '🎡', '🎢', '🎠', '⛲', '⛱️',
    '🏖️', '🏝️', '🏜️', '🌋', '⛰️', '🏔️', '🗻', '🏕️', '⛺', '🏠',
    '🏡', '🏘️', '🏚️', '🏗️', '🏭', '🏢', '🏬', '🏣', '🏤', '🏥',
    '🏦', '🏨', '🏪', '🏫', '🏩', '💒', '🏛️', '⛪', '🕌', '🕍',
    '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
    '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️',
    '✝️', '☪️', '🕉️', '☸️', '✡️', '🔯', '🕎', '☯️', '☦️', '🛐',
    '⛎', '♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐',
    '♑', '♒', '♓', '🆔', '⚛️', '🉑', '☢️', '☣️', '📴', '📳',
    '🆘', '❌', '⭕', '🛑', '⛔', '📛', '🚫', '💯', '💢', '♨️',
    '♻️', '✅', '🈯', '💹', '❇️', '✳️', '❎', '🌐', '💠', 'Ⓜ️',
    '🌀', '💤', '🏧', '🚾', '♿', '🅿️', '🈳', '🈂️', '🛂', '🛃',
    '🛄', '🛅', '🚹', '🚺', '🚼', '🚻', '🚮', '🎦', '📶', '🈁',
    'ℹ️', '🔤', '🔡', '🔠', '🆖', '🆗', '🆙', '🆒', '🆕', '🆓',
    '0️⃣', '1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣',
    '🔟', '🔢', '#️⃣', '*️⃣', '▶️', '⏸️', '⏯️', '⏹️', '⏺️', '⏭️',
    '⏮️', '⏩', '⏪', '⏫', '⏬', '◀️', '🔼', '🔽', '➡️', '⬅️',
    '⬆️', '⬇️', '↗️', '↘️', '↙️', '↖️', '↕️', '↔️', '↪️', '↩️',
    '🔀', '🔁', '🔂', '🔄', '🔃', '🎵', '🎶', '➕', '➖', '➗',
    '✖️', '💲', '💱', '™️', '©️', '®️', '〰️', '➰', '➿', '🔚',
    '🔙', '🔛', '🔜', '🔝', '✔️', '☑️', '🔘', '⚪', '⚫', '🔴',
    '🔵', '🟠', '🟡', '🟢', '🟣', '🟤', '🔶', '🔷', '🔸', '🔹',
    '🔺', '🔻', '💠', '🔳', '🔲', '▪️', '▫️', '◾', '◽', '◼️',
    '◻️', '🟥', '🟧', '🟨', '🟩', '🟦', '🟪', '🟫', '⬛', '⬜',
    '🔈', '🔇', '🔉', '🔊', '🔔', '🔕', '📣', '📢', '♠️', '♣️',
    '♥️', '♦️', '🃏', '🎴', '🀄', '🕐', '🕑', '🕒', '🕓', '🕔',
    '🕕', '🕖', '🕗', '🕘', '🕙', '🕚', '🕛', '🕜', '🕝', '🕞',
    '🕟', '🕠', '🕡', '🕢', '🕣', '🕤', '🕥', '🕦', '🕧', '☀️',
    '🌞', '⭐', '🌟', '💫', '✨', '⚡', '☄️', '💥', '🔥', '🌈',
    '☔', '❄️', '☃️', '⛄', '🌊', '💧', '💦'
  ];
  const filteredEmojis = popularEmojis.filter(emoji =>
    emojiSearch === '' || emoji.includes(emojiSearch)
  );

  // Program helpers for Suggested Program section
  const validateProgram = (program) => {
    if (!program || typeof program !== 'object') return { name: 'Unknown Program', description: 'Program description', pricePerMonth: 0, isMostPopular: false, minimumTerm: 12, features: {}, showcaseFeatures: [] };
    return {
      name: program.name || 'Unknown Program',
      description: program.description || 'Program description',
      pricePerMonth: program.pricePerMonth || 0,
      isMostPopular: program.isMostPopular || false,
      minimumTerm: program.minimumTerm || 12,
      features: program.features && typeof program.features === 'object' ? program.features : {},
      showcaseFeatures: Array.isArray(program.showcaseFeatures) ? program.showcaseFeatures : []
    };
  };
  const getProgramColor = (programName) => {
    const colors = { 'Business Launch Blueprint': 'from-blue-500 to-blue-600', 'Operational Excellence': 'from-green-500 to-green-600', 'Financial Mastery': 'from-purple-500 to-purple-600', 'Sales & Marketing Excellence': 'from-pink-500 to-pink-600', 'Technology & Innovation': 'from-indigo-500 to-indigo-600' };
    return colors[programName] || 'from-red-500 to-red-600';
  };
  const getOrderedPrograms = () => {
    if (!programs) return [];
    const tailOrder = ['Pro', 'Premier', 'BBA Bundle'];
    const orderIndex = (key) => { const i = tailOrder.indexOf(key); return i >= 0 ? i + 1000 : 0; };
    return Object.entries(programs)
      .filter(([key, program]) => !isRetiredProgram(key, program))
      .sort(([keyA], [keyB]) => orderIndex(keyA) - orderIndex(keyB));
  };
  const getMonthlyPrice = (pricePerMonth) => {
    if (billingCycle === 'annual' && billingCycles?.annual) return Math.round(pricePerMonth * (1 - billingCycles.annual.discount / 100));
    if (billingCycle === 'quarterly' && billingCycles?.quarterly) return Math.round(pricePerMonth * (1 - billingCycles.quarterly.discount / 100));
    return pricePerMonth;
  };
  const getAnnualPrice = (pricePerMonth) => {
    if (billingCycle === 'annual' && billingCycles?.annual) return Math.round(pricePerMonth * billingCycles.annual.months * (1 - billingCycles.annual.discount / 100));
    if (billingCycle === 'quarterly' && billingCycles?.quarterly) return Math.round(pricePerMonth * billingCycles.quarterly.months * (1 - billingCycles.quarterly.discount / 100));
    return pricePerMonth * 12;
  };
  const getQuarterlyPrice = (pricePerMonth) => {
    if (billingCycle === 'quarterly' && billingCycles?.quarterly) return Math.round(pricePerMonth * billingCycles.quarterly.months * (1 - billingCycles.quarterly.discount / 100));
    return pricePerMonth * 3;
  };
  const getDepartmentColor = (department) => {
    const colors = { 'General': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200', 'Sales': 'bg-blue-100 text-blue-800 dark:bg-blue-700 dark:text-blue-200', 'Operations': 'bg-green-100 text-green-800 dark:bg-green-700 dark:text-green-200', 'Finance': 'bg-purple-100 text-purple-800 dark:bg-purple-700 dark:text-purple-200', 'Technology': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-700 dark:text-indigo-200', 'Marketing': 'bg-pink-100 text-pink-800 dark:bg-pink-700 dark:text-pink-200', 'HR': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-700 dark:text-yellow-200' };
    return colors[department] || colors['General'];
  };
  const getShowcaseFeatures = (programKey) => {
    const program = programs[programKey];
    if (!program || !program.showcaseFeatures) return [];
    return (program.showcaseFeatures || [])
      .map((item) => (typeof item === 'string' ? { text: item, showcase: true, description: '' } : { ...item, description: item.description || '' }))
      .filter((item) => item.showcase && item.text?.trim());
  };
  const showItemDetails = (item) => { setSelectedItem(item); setShowItemModal(true); };
  const closeItemModal = () => { setShowItemModal(false); setSelectedItem(null); };
  const showShowcaseFeatureDetails = (feature) => setSelectedShowcaseFeature(feature);
  const closeShowcaseFeatureModal = () => setSelectedShowcaseFeature(null);

  // Add item to SWOT category
  const addSWOTItem = (category, newItem = '') => {
    setEditableSWOT(prev => ({
      ...prev,
      [category]: [...prev[category], newItem]
    }));
  };

  // Delete item from SWOT category
  const deleteSWOTItem = (category, index) => {
    setEditableSWOT(prev => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== index)
    }));
  };

  // Update item in SWOT category
  const updateSWOTItem = (category, index, value) => {
    setEditableSWOT(prev => ({
      ...prev,
      [category]: prev[category].map((item, i) => i === index ? value : item)
    }));
  };

  // Move item up in SWOT category
  const moveSWOTItemUp = (category, index) => {
    if (index === 0) return;
    setEditableSWOT(prev => {
      const newArray = [...prev[category]];
      [newArray[index - 1], newArray[index]] = [newArray[index], newArray[index - 1]];
      return {
        ...prev,
        [category]: newArray
      };
    });
  };

  // Move item down in SWOT category
  const moveSWOTItemDown = (category, index) => {
    setEditableSWOT(prev => {
      if (index >= prev[category].length - 1) return prev;
      const newArray = [...prev[category]];
      [newArray[index], newArray[index + 1]] = [newArray[index + 1], newArray[index]];
      return {
        ...prev,
        [category]: newArray
      };
    });
  };
  
  // Lead Source data for pie chart
  const leadSourceData = useMemo(() => {
    const referralPercentage = salesData.referralBusinessPercentage || 0;
    const organic = referralPercentage;
    const nonOrganic = Math.max(0, 100 - referralPercentage);
    
    return [
      { name: 'Organic (Referral)', value: organic, color: '#10B981' },
      { name: 'Non-Organic', value: nonOrganic, color: '#3B82F6' }
    ];
  }, [salesData.referralBusinessPercentage]);
  
  // Repeat Customer data
  const repeatCustomerPercentage = salesData.repeatCustomerPercentage || 0;
  
  // Close Ratio analysis
  const closeRatio = salesData.closeRatio || 0;
  const closeRatioStatus = closeRatio < 30 ? 'weak' : closeRatio < 50 ? 'average' : 'strong';
  const closeRatioColor = closeRatio < 30 ? 'red' : closeRatio < 50 ? 'yellow' : 'green';
  
  // Revenue analysis and projections
  const revenueAnalysis = useMemo(() => {
    const oneYearAgo = parseFloat(salesData.oneYearAgo || salesData.revenueData?.oneYearAgo || 0);
    const currentYear = parseFloat(salesData.currentYearProjection || salesData.revenueData?.currentYearProjection || 0);
    const nextYearGoal = parseFloat(salesData.nextYearGoal || salesData.revenueData?.nextYearGoal || salesData.goalsData?.annualRevenueTarget || 0);
    
    const calculatedYearOverYearGrowth = oneYearAgo > 0 ? ((currentYear - oneYearAgo) / oneYearAgo) * 100 : 0;
    // Use custom growth percentage if set, otherwise use calculated
    const yearOverYearGrowth = customGrowthPercentage !== null ? customGrowthPercentage : calculatedYearOverYearGrowth;
    const goalGrowth = currentYear > 0 ? ((nextYearGoal - currentYear) / currentYear) * 100 : 0;
    const totalGrowth = oneYearAgo > 0 ? ((nextYearGoal - oneYearAgo) / oneYearAgo) * 100 : 0;
    
    // Calculate growth rate (as decimal) for projections - use custom if set
    const growthRate = customGrowthPercentage !== null 
      ? 1 + (customGrowthPercentage / 100)
      : (oneYearAgo > 0 ? (currentYear / oneYearAgo) : 1);
    
    // Use lead's projected next year revenue if available, otherwise calculate based on growth rate
    const projectedNextYear = nextYearGoal > 0 ? nextYearGoal : currentYear * growthRate;
    const projectedTwoYears = projectedNextYear * growthRate;
    const projectedThreeYears = projectedTwoYears * growthRate;
    
    return {
      yearOverYearGrowth: yearOverYearGrowth.toFixed(1),
      calculatedYearOverYearGrowth: calculatedYearOverYearGrowth.toFixed(1),
      goalGrowth: goalGrowth.toFixed(1),
      totalGrowth: totalGrowth.toFixed(1),
      growthRate: growthRate - 1, // Convert to percentage
      oneYearAgo,
      currentYear,
      nextYearGoal,
      projectedNextYear,
      projectedTwoYears,
      projectedThreeYears
    };
  }, [salesData, customGrowthPercentage]);
  
  // Revenue data for line chart (includes actual and projected)
  const revenueData = useMemo(() => {
    const currentYearNum = new Date().getFullYear();
    const { oneYearAgo, currentYear, nextYearGoal, projectedNextYear, projectedTwoYears, projectedThreeYears } = revenueAnalysis;
    
    return {
      all: [
        { year: `${currentYearNum - 1}`, revenue: oneYearAgo, type: 'actual' },
        { year: `${currentYearNum}`, revenue: currentYear, type: 'actual' },
        { year: `${currentYearNum + 1}`, revenue: projectedNextYear, type: 'projected' },
        { year: `${currentYearNum + 2}`, revenue: projectedTwoYears, type: 'projected' },
        { year: `${currentYearNum + 3}`, revenue: projectedThreeYears, type: 'projected' }
      ],
      actual: [
        { year: `${currentYearNum - 1}`, revenue: oneYearAgo },
        { year: `${currentYearNum}`, revenue: currentYear }
      ],
      projected: [
        { year: `${currentYearNum}`, revenue: currentYear },
        { year: `${currentYearNum + 1}`, revenue: projectedNextYear },
        { year: `${currentYearNum + 2}`, revenue: projectedTwoYears },
        { year: `${currentYearNum + 3}`, revenue: projectedThreeYears }
      ]
    };
  }, [revenueAnalysis]);

  // Department owner rating row (stacked list): fixed name column keeps stars close and aligned
  const renderStarRating = (rating, label, emoji) => (
    <div className="flex items-center justify-center gap-5 py-3 border-b border-gray-200 dark:border-gray-700 last:border-b-0">
      <div className="flex items-center gap-3 w-44 sm:w-48">
        <span className="w-10 h-10 rounded-xl bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center text-xl flex-shrink-0">
          {emoji}
        </span>
        <span className="text-lg font-semibold text-gray-900 dark:text-white truncate">{label}</span>
      </div>
      {rating > 0 ? (
        <>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`w-6 h-6 ${star <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300 dark:text-gray-600'}`}
              />
            ))}
          </div>
          <span className="w-12 text-lg font-bold text-gray-900 dark:text-white">
            {rating}<span className="text-gray-400 dark:text-gray-500 font-medium text-base">/5</span>
          </span>
        </>
      ) : (
        <span className="text-base italic text-gray-400 dark:text-gray-500">Not available</span>
      )}
    </div>
  );

  // Company logo (never the contact's headshot) — same source the CRM account/lead cards use.
  const companyLogoUrl = getCrmCompanyLogoDisplayUrl(lead);

  // Company rating out of 100 (average of all BBA ratings * 20)
  const companyRating = (() => {
    const ratings = [bbaRating, operationsBBARating, financeBBARating, marketingBBARating, techBBARating].filter((r) => r > 0);
    const avg = ratings.length > 0 ? ratings.reduce((sum, r) => sum + r, 0) / ratings.length : 0;
    return avg * 20;
  })();

  const gpmValue = financeData.grossProfitMargin ? parseFloat(financeData.grossProfitMargin || 0) : null;
  const gpmTone = gpmValue === null ? 'gray' : gpmValue < 30 ? 'red' : gpmValue <= 40 ? 'amber' : 'green';
  const npmValue = financeData.netProfitMargin ? parseFloat(financeData.netProfitMargin || 0) : null;
  const npmTone = npmValue === null ? 'gray' : npmValue < 5 ? 'red' : npmValue <= 10 ? 'amber' : 'green';
  const closeRatioTone = closeRatioStatus === 'weak' ? 'red' : closeRatioStatus === 'average' ? 'amber' : 'green';
  const toneLabel = { red: 'Below target', amber: 'On track', green: 'Strong' };

  const backTo = isStartSection ? '/start' : undefined;

  // Dummy signup-flow preview under Getting Started: personalise with the suggested program when one is set.
  // A retired standard program is never previewed; a confirmed custom proposal is.
  const previewProgram = suggestedProgramKey && programs?.[suggestedProgramKey] && !isRetiredProgram(suggestedProgramKey, programs[suggestedProgramKey])
    ? validateProgram(programs[suggestedProgramKey])
    : null;
  const proposedMonthly = Number(lead?.proposedProgram?.totalPricePerMonth) || 0;
  const previewPrice = previewProgram
    ? billingCycle === 'quarterly' && billingCycles?.quarterly
      ? `$${getQuarterlyPrice(previewProgram.pricePerMonth).toLocaleString()}`
      : `$${getMonthlyPrice(previewProgram.pricePerMonth).toLocaleString()}`
    : proposedMonthly > 0
      ? `$${Math.round(proposedMonthly).toLocaleString()}`
      : '$2,500';
  const previewProgramName = previewProgram?.name
    || lead?.proposedProgram?.programName?.replace(/\s*Program\s*$/, '')
    || 'Custom';
  const previewBillingText = billingCycle === 'quarterly' ? 'per quarter' : billingCycle === 'annual' ? 'per year' : 'per month';
  const standardPreviewCards = getOrderedPrograms().map(([programKey, program]) => {
    const vp = validateProgram(program);
    return {
      name: vp.name,
      price: `$${getMonthlyPrice(vp.pricePerMonth).toLocaleString()}`,
      isMostPopular: Boolean(vp.isMostPopular),
      isSuggested: suggestedProgramKey === programKey,
      features: getShowcaseFeatures(programKey).map((f) => ({
        icon: availableItems.find((it) => it.name === f.text)?.icon || '⭐',
        text: f.text,
      })),
    };
  });
  const previewProgramCards = standardPreviewCards.length > 0
    ? standardPreviewCards
    : [{
      name: previewProgramName,
      price: previewPrice,
      isMostPopular: false,
      isSuggested: true,
      features: [
        { icon: '🗺️', text: 'Built around your assessment' },
        { icon: '🧩', text: 'Only the services you need' },
        { icon: '📞', text: '1:1 strategy calls' },
      ],
    }];
  const previewSigner =
    lead?.contactName
    || [lead?.firstName, lead?.lastName].filter(Boolean).join(' ')
    || lead?.name
    || 'Owner';
  const previewFirstName = lead?.firstName || String(previewSigner || '').split(' ')[0] || '';
  const isComplete = assessmentStatus === 'complete';

  if (loading) {
    return (
      <div className="relative min-h-screen p-4 md:p-6">
        <PortalBackground />
        {isStartSection && <StartSidebar />}
        <div className="relative max-w-screen-2xl mx-auto flex flex-col items-center justify-center min-h-[70vh]">
          <BBALoader size={96} />
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading assessment analysis…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative min-h-screen p-4 md:p-6">
        <PortalBackground />
        {isStartSection && <StartSidebar />}
        <div className="relative max-w-screen-2xl mx-auto">
          <BackButton to={backTo} />
          <div className={`${CARD} mt-6 p-10 flex flex-col items-center text-center`}>
            <IconTile emoji="❗" tone="red" size="lg" className="mb-3" />
            <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen p-4 md:p-6">
      {/* Same ambient BBA background as the Lead Overview page */}
      <PortalBackground />
      {isStartSection && <StartSidebar />}
      <div className="relative max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="relative flex items-start justify-center mb-8 min-h-[2.5rem]">
          <div className="absolute left-0 top-0">
            <BackButton to={backTo} />
          </div>
          <div className="flex flex-col items-center text-center px-14 pt-1">
            {companyLogoUrl && (
              <div className="mb-3 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white p-2 shadow-sm">
                <img
                  src={companyLogoUrl}
                  alt={`${lead?.company || 'Company'} logo`}
                  className="h-16 w-auto max-w-[220px] object-contain"
                />
              </div>
            )}
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-red-600 dark:text-red-400">Assessment Analysis</p>
            <h1 className="mt-1 text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
              {lead?.company || 'N/A'}
            </h1>
          </div>
          <div className="absolute right-0 top-0">
            <button
              type="button"
              onClick={() => saveAssessmentStatus(isComplete ? 'in_review' : 'complete')}
              disabled={savingStatus}
              className={`inline-flex items-center gap-2 h-10 px-3 rounded-full text-xs font-semibold border transition-colors ${
                isComplete
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50'
                  : 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50'
              } ${savingStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={isComplete ? 'Put into In Review' : 'Mark Complete'}
            >
              {isComplete ? <CheckCircle className="w-4 h-4" /> : <FileCheck className="w-4 h-4" />}
              <span className="hidden sm:inline">{isComplete ? 'Complete' : 'In Review'}</span>
            </button>
          </div>
        </div>

        {/* Scorecard: company rating + department owner ratings */}
        <section className={`${CARD} p-6 md:p-8 mb-6`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            <div className={`${SUBCARD} flex flex-col items-center justify-center text-center p-6`}>
              <ScoreRing value={companyRating} size={200} stroke={14} />
              <p className="mt-3 text-sm font-semibold text-gray-900 dark:text-white">Company Rating</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Average of BBA department ratings</p>
            </div>
            <div className={`${SUBCARD} px-5 pb-1`}>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 text-center py-3 border-b border-gray-200 dark:border-gray-700">
                Owner Ratings by Department
              </p>
              {renderStarRating(ownerRating, 'Sales', PORTAL_EMOJI.sales)}
              {renderStarRating(operationsOwnerRating, 'Operations', PORTAL_EMOJI.operations)}
              {renderStarRating(financeOwnerRating, 'Finance', PORTAL_EMOJI.finance)}
              {renderStarRating(marketingOwnerRating, 'Marketing', PORTAL_EMOJI.marketing)}
              {renderStarRating(techOwnerRating, 'Technology', PORTAL_EMOJI.technology)}
            </div>
          </div>
        </section>

        {/* Close Ratio, Gross Profit Margin, Net Profit Margin */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <ThresholdMetric
            emoji="🤝"
            label="Close Ratio"
            value={closeRatio > 0 ? closeRatio : null}
            display={`${closeRatio.toFixed(1)}%`}
            tone={closeRatioTone}
            statusLabel={toneLabel[closeRatioTone]}
            segments={[{ to: 30, tone: 'red' }, { to: 50, tone: 'amber' }, { to: 100, tone: 'green' }]}
            scaleMax={100}
            ticks={[{ at: 0, label: '0%' }, { at: 30, label: '30%' }, { at: 50, label: '50%' }, { at: 100, label: '100%' }]}
          />
          <ThresholdMetric
            emoji="💰"
            label="Gross Profit Margin"
            value={gpmValue}
            display={gpmValue !== null ? `${gpmValue.toFixed(1)}%` : ''}
            tone={gpmTone}
            statusLabel={toneLabel[gpmTone]}
            segments={[{ to: 30, tone: 'red' }, { to: 40, tone: 'amber' }, { to: 100, tone: 'green' }]}
            scaleMax={100}
            ticks={[{ at: 0, label: '0%' }, { at: 30, label: '30%' }, { at: 40, label: '40%' }, { at: 100, label: '100%' }]}
          />
          <ThresholdMetric
            emoji="🏦"
            label="Net Profit Margin"
            value={npmValue}
            display={npmValue !== null ? `${npmValue.toFixed(1)}%` : ''}
            tone={npmTone}
            statusLabel={toneLabel[npmTone]}
            segments={[{ to: 5, tone: 'red' }, { to: 10, tone: 'amber' }, { to: 50, tone: 'green' }]}
            scaleMax={50}
            ticks={[{ at: 0, label: '0%' }, { at: 5, label: '5%' }, { at: 10, label: '10%' }, { at: 50, label: '50%' }]}
          />
        </div>

        {/* Revenue Trend */}
        <section className={`${CARD} p-6 md:p-8 mb-6`}>
          <SectionHeader emoji="📈" title="Revenue Trend" subtitle="Actual revenue with a projected three-year path at the current growth rate." />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            <StatTile label="Previous Year" value={revenueAnalysis.oneYearAgo > 0 ? `$${revenueAnalysis.oneYearAgo.toLocaleString()}` : '—'} />
            <StatTile label="Current Year" value={revenueAnalysis.currentYear > 0 ? `$${revenueAnalysis.currentYear.toLocaleString()}` : '—'} />
            <StatTile label="Projected Next Year" value={revenueAnalysis.projectedNextYear > 0 ? `$${revenueAnalysis.projectedNextYear.toLocaleString()}` : '—'} tone="red" />
          </div>
          {revenueData.all.some((d) => d.revenue > 0) ? (
            <div>
              <div className="mb-4 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-sm">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-0.5 rounded bg-red-600" />
                    <span className="text-gray-600 dark:text-gray-400">Actual</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-0 border-t-2 border-dashed border-red-500" />
                    <span className="text-gray-600 dark:text-gray-400">Projected</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 dark:text-gray-400">Growth rate</span>
                  {isLocked ? (
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{revenueAnalysis.yearOverYearGrowth}%</span>
                  ) : isEditingGrowth ? (
                    <div className="flex items-center gap-1.5">
                      <input
                        type="number"
                        step="0.1"
                        value={customGrowthPercentage !== null ? customGrowthPercentage : parseFloat(revenueAnalysis.calculatedYearOverYearGrowth)}
                        onChange={(e) => setCustomGrowthPercentage(parseFloat(e.target.value) || 0)}
                        onBlur={() => setIsEditingGrowth(false)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setIsEditingGrowth(false);
                          }
                        }}
                        className={`${INPUT} w-24 text-center`}
                        autoFocus
                      />
                      <span className="text-gray-500 dark:text-gray-400">%</span>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsEditingGrowth(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-semibold text-gray-900 dark:text-white hover:border-red-300 dark:hover:border-red-600 transition-colors"
                      title="Edit growth rate"
                    >
                      {revenueAnalysis.yearOverYearGrowth}%
                      <Edit2 className="w-3 h-3 text-gray-400" />
                    </button>
                  )}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart>
                  <defs>
                    <linearGradient id="aaActualFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#dc2626" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#dc2626" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="aaProjectedFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#dc2626" stopOpacity={0.16} />
                      <stop offset="100%" stopColor="#dc2626" stopOpacity={0.01} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={darkMode ? '#374151' : '#e5e7eb'} />
                  <XAxis
                    dataKey="year"
                    stroke={darkMode ? '#9ca3af' : '#6b7280'}
                    type="category"
                    allowDuplicatedCategory={false}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    stroke={darkMode ? '#9ca3af' : '#6b7280'}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    formatter={(value) => `$${parseInt(value).toLocaleString()}`}
                    contentStyle={{
                      backgroundColor: darkMode ? '#111827' : '#ffffff',
                      border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                      borderRadius: 12,
                      color: darkMode ? '#f3f4f6' : '#111827',
                    }}
                  />
                  <Area
                    type="monotone"
                    data={revenueData.actual}
                    dataKey="revenue"
                    stroke="#dc2626"
                    strokeWidth={2.5}
                    fill="url(#aaActualFill)"
                    name="Actual Revenue"
                    dot={{ fill: '#dc2626', r: 5, strokeWidth: 0 }}
                    connectNulls={false}
                  >
                    <LabelList
                      dataKey="revenue"
                      position="top"
                      formatter={(value, entry, index) => {
                        try {
                          if (value === undefined || value === null) return '';
                          const currentYearNum = new Date().getFullYear();
                          // Access year from the data array directly using index
                          const dataPoint = revenueData.actual[index];
                          if (dataPoint && dataPoint.year === `${currentYearNum}`) {
                            return `$${(value / 1000).toFixed(0)}k`;
                          }
                          return '';
                        } catch (error) {
                          return '';
                        }
                      }}
                      style={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: '12px', fontWeight: '500' }}
                    />
                  </Area>
                  <Area
                    type="monotone"
                    data={revenueData.projected}
                    dataKey="revenue"
                    stroke="#dc2626"
                    strokeWidth={2.5}
                    strokeDasharray="6 5"
                    fill="url(#aaProjectedFill)"
                    name="Projected Revenue"
                    dot={{ fill: darkMode ? '#111827' : '#ffffff', r: 5, stroke: '#dc2626', strokeWidth: 2 }}
                    connectNulls={false}
                  >
                    <LabelList
                      dataKey="revenue"
                      position="top"
                      formatter={(value, entry, index) => {
                        try {
                          if (value === undefined || value === null) return '';
                          const currentYearNum = new Date().getFullYear();
                          // Access year from the data array directly using index
                          // Next year is at index 1 in projected array (index 0 is current year)
                          const dataPoint = revenueData.projected[index];
                          if (dataPoint && dataPoint.year === `${currentYearNum + 1}`) {
                            return `$${(value / 1000).toFixed(0)}k`;
                          }
                          return '';
                        } catch (error) {
                          return '';
                        }
                      }}
                      style={{ fill: darkMode ? '#9ca3af' : '#6b7280', fontSize: '12px', fontWeight: '500' }}
                    />
                  </Area>
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-gray-400 dark:text-gray-500 italic text-center py-12">No revenue data available</p>
          )}
        </section>

        {/* Website Audit */}
        {(effectiveLeadIdRef.current || leadId) && (
          <section className={`${CARD} p-6 md:p-8 mb-6`}>
            <WebsiteAuditOverviewSection
              accountId={effectiveLeadIdRef.current || leadId}
              defaultUrl={lead?.website || lead?.discovery?.website || ''}
              darkMode={darkMode}
              readOnly={isLocked}
            />
          </section>
        )}

        {/* Goals */}
        <section className={`${CARD} p-6 md:p-8 mb-6`}>
          <SectionHeader
            emoji={PORTAL_EMOJI.goals}
            title="Goals"
            right={isLocked ? null : (
              <EditToolbar
                editing={isEditingGoals}
                label="Edit Goals"
                onEdit={() => setIsEditingGoals(true)}
                onSave={saveGoalsData}
                onCancel={() => {
                  setEditableGoals(parseGoals(lead?.goals));
                  setIsEditingGoals(false);
                }}
              />
            )}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {editableGoals.length === 0 && !isEditingGoals && (
              <p className="col-span-full text-center text-gray-400 dark:text-gray-500 italic py-8">No goals yet. Click Edit to add.</p>
            )}
            {editableGoals.map((card, idx) => (
              <div key={idx} className={`${SUBCARD} p-5 relative`}>
                {isEditingGoals ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-end gap-1">
                      <button type="button" onClick={() => moveGoalCardUp(idx)} disabled={idx === 0} className={ICON_BTN} title="Move up">
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => moveGoalCardDown(idx)} disabled={idx >= editableGoals.length - 1} className={ICON_BTN} title="Move down">
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => removeGoalCard(idx)} className={DANGER_ICON_BTN} title="Remove card">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Label</label>
                      <input
                        type="text"
                        value={card.header || ''}
                        onChange={(e) => updateGoalCard(idx, 'header', e.target.value)}
                        placeholder="e.g. 1 Year, 3 Year, Short-term, Quarterly"
                        className={INPUT}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Goal</label>
                      <textarea
                        value={card.goal || ''}
                        onChange={(e) => updateGoalCard(idx, 'goal', e.target.value)}
                        placeholder="Goal..."
                        rows={4}
                        className={`${INPUT} resize-y`}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center text-center">
                    <span className="inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-200">
                      {card.header || '—'}
                    </span>
                    <p className="mt-3 text-lg font-semibold leading-snug text-gray-900 dark:text-white whitespace-pre-wrap">
                      {card.goal || <span className="italic text-gray-400 dark:text-gray-500 font-normal">No goal set</span>}
                    </p>
                  </div>
                )}
              </div>
            ))}
            {isEditingGoals && (
              <button type="button" onClick={addGoalCard} className={`${DASHED_ADD} flex-col py-8 min-h-[140px]`}>
                <Plus className="w-7 h-7" />
                <span className="text-sm">Add card</span>
              </button>
            )}
          </div>
        </section>

        {/* SWOT Analysis */}
        <section className={`${CARD} p-6 md:p-8 mb-6`}>
          <SectionHeader
            emoji="🧭"
            title="SWOT Analysis"
            right={isLocked ? null : (
              <EditToolbar
                editing={isEditingSWOT}
                label="Edit SWOT Analysis"
                onEdit={() => setIsEditingSWOT(true)}
                onSave={saveSWOTData}
                onCancel={() => {
                  // Reset to original data
                  const customSWOT = lead?.customSWOT || {};
                  setEditableSWOT({
                    strengths: customSWOT.strengths || swotData.strengths || [],
                    weaknesses: customSWOT.weaknesses || swotData.weaknesses || [],
                    opportunities: customSWOT.opportunities || swotData.opportunities || [],
                    threats: customSWOT.threats || swotData.threats || [],
                  });
                  setIsEditingSWOT(false);
                }}
              />
            )}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {SWOT_COLUMNS.map(({ key, title, tone, emoji, itemEmoji }) => {
              const items = editableSWOT[key] || [];
              return (
                <div key={key} className={`${SUBCARD} relative overflow-hidden p-5`}>
                  <div className={`absolute inset-x-0 top-0 h-1 ${TONES[tone].bar}`} />
                  <div className="relative flex flex-col items-center text-center mb-4">
                    <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
                      <span className="text-xl leading-none" aria-hidden="true">{emoji}</span>
                      <span>{title}</span>
                    </h3>
                    {isEditingSWOT && (
                      <button type="button" onClick={() => addSWOTItem(key, '')} className={`absolute right-0 top-0 ${ICON_BTN}`} title="Add item">
                        <Plus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {items.length > 0 ? (
                    <ul className="space-y-3">
                      {items.map((item, index) => (
                        <li key={index} className="flex items-start gap-2.5">
                          <span className="text-base leading-6 flex-shrink-0">{itemEmoji}</span>
                          {isEditingSWOT ? (
                            <div className="flex-1 flex items-center gap-1">
                              <input type="text" value={item} onChange={(e) => updateSWOTItem(key, index, e.target.value)} className={`${INPUT} text-base`} />
                              <button type="button" onClick={() => moveSWOTItemUp(key, index)} disabled={index === 0} className={ICON_BTN} title="Move up">
                                <ArrowUp className="w-3.5 h-3.5" />
                              </button>
                              <button type="button" onClick={() => moveSWOTItemDown(key, index)} disabled={index >= items.length - 1} className={ICON_BTN} title="Move down">
                                <ArrowDown className="w-3.5 h-3.5" />
                              </button>
                              <button type="button" onClick={() => deleteSWOTItem(key, index)} className={DANGER_ICON_BTN} title="Remove">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <span className="text-base leading-6 text-gray-800 dark:text-gray-200">{item}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-base text-gray-400 dark:text-gray-500 italic text-center py-2">N/A</p>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Key Implementations */}
        <section className={`${CARD} p-6 md:p-8 mb-6`}>
          <SectionHeader
            emoji="⚡"
            title="Key Implementations"
            right={isLocked ? null : (
              <EditToolbar
                editing={isEditingLevers}
                label="Edit Key Implementations"
                onEdit={() => setIsEditingLevers(true)}
                onSave={saveLeversData}
                onCancel={() => {
                  setEditableLevers(parseImmediateLevers(lead?.immediateLevers));
                  setIsEditingLevers(false);
                  setShowEmojiPicker(null);
                  setEmojiSearch('');
                }}
              />
            )}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {editableLevers.length === 0 && !isEditingLevers && (
              <p className="col-span-full text-center text-gray-400 dark:text-gray-500 italic py-8">No cards yet. Click Edit to add.</p>
            )}
            {editableLevers.map((card, originalIdx) => {
              const dept = LEVER_DEPARTMENTS.find((d) => d.id === card.department);
              return (
                <div key={originalIdx} className={`${SUBCARD} p-5 relative`}>
                  {isEditingLevers ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <button
                          type="button"
                          onClick={() => setShowEmojiPicker(originalIdx)}
                          className="w-11 h-11 text-2xl rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-red-300 dark:hover:border-red-600 flex items-center justify-center transition-colors"
                          title="Pick emoji"
                        >
                          {card.emoji || '😊'}
                        </button>
                        <div className="flex items-center gap-1">
                          <button type="button" onClick={() => moveLeverCardUp(originalIdx)} disabled={originalIdx === 0} className={ICON_BTN} title="Move up">
                            <ArrowUp className="w-3.5 h-3.5" />
                          </button>
                          <button type="button" onClick={() => moveLeverCardDown(originalIdx)} disabled={originalIdx >= editableLevers.length - 1} className={ICON_BTN} title="Move down">
                            <ArrowDown className="w-3.5 h-3.5" />
                          </button>
                          <button type="button" onClick={() => removeLeverCard(originalIdx)} className={DANGER_ICON_BTN} title="Remove card">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <select value={card.department || ''} onChange={(e) => updateLeverCard(originalIdx, 'department', e.target.value)} className={INPUT}>
                        <option value="">Department</option>
                        {LEVER_DEPARTMENTS.map((d) => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={card.header || ''}
                        onChange={(e) => updateLeverCard(originalIdx, 'header', e.target.value)}
                        placeholder="Header"
                        className={`${INPUT} font-medium`}
                      />
                      <textarea
                        value={card.description || ''}
                        onChange={(e) => updateLeverCard(originalIdx, 'description', e.target.value)}
                        placeholder="Description"
                        rows={3}
                        className={`${INPUT} resize-y`}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center text-center">
                      {card.emoji && <IconTile emoji={card.emoji} plain size="lg" className="mb-3" />}
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">{card.header || '—'}</h3>
                      {card.department && (
                        <span className={`mt-2 inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${departmentPillClasses[dept?.color || 'red']}`}>
                          {dept?.name || card.department}
                        </span>
                      )}
                      {card.description && (
                        <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{card.description}</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {isEditingLevers && (
              <button type="button" onClick={addLeverCard} className={`${DASHED_ADD} flex-col py-8 min-h-[140px]`}>
                <Plus className="w-7 h-7" />
                <span className="text-sm">Add card</span>
              </button>
            )}
          </div>

          {/* Implementation Results — lives at the bottom of Key Implementations */}
          <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
            <div className="relative flex items-center justify-center mb-4 min-h-[2rem]">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
                <span className="text-xl leading-none" aria-hidden="true">🏆</span>
                <span>Implementation Results</span>
              </h3>
              {!isLocked && (
              <div className="absolute right-0 top-1/2 -translate-y-1/2">
                <EditToolbar
                  editing={isEditingImplementationResults}
                  label="Edit Implementation Results"
                  onEdit={() => setIsEditingImplementationResults(true)}
                  onSave={saveImplementationResultsData}
                  onCancel={() => {
                    setEditableImplementationResults(parseImplementationResults(lead?.implementationResults));
                    setIsEditingImplementationResults(false);
                  }}
                />
              </div>
              )}
            </div>
            {editableImplementationResults.length === 0 && !isEditingImplementationResults && (
              <p className="text-center text-gray-400 dark:text-gray-500 italic py-4">No results yet. Click Edit to add.</p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {editableImplementationResults.map((text, idx) => (
                <div key={idx} className={`${SUBCARD} px-4 py-3 flex items-center`}>
                  {isEditingImplementationResults ? (
                    <div className="flex items-center gap-1.5 w-full">
                      <input
                        type="text"
                        value={text}
                        onChange={(e) => updateImplementationResultRow(idx, e.target.value)}
                        placeholder="Add result..."
                        className={`${INPUT} font-semibold`}
                      />
                      <button type="button" onClick={() => moveImplementationResultUp(idx)} disabled={idx === 0} className={ICON_BTN} title="Move up">
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => moveImplementationResultDown(idx)} disabled={idx >= editableImplementationResults.length - 1} className={ICON_BTN} title="Move down">
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => removeImplementationResultRow(idx)} className={DANGER_ICON_BTN} title="Remove">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <p className="text-base font-semibold text-gray-900 dark:text-white">
                      {text || <span className="italic text-gray-400 dark:text-gray-500 font-normal">—</span>}
                    </p>
                  )}
                </div>
              ))}
              {isEditingImplementationResults && (
                <button type="button" onClick={addImplementationResultRow} className={`${DASHED_ADD} px-4 py-3`}>
                  <Plus className="w-5 h-5" />
                  <span className="text-sm">Add row</span>
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Our Process */}
        <section className={`${CARD} p-6 md:p-8 mb-6`}>
          <SectionHeader emoji="🛣️" title="Our Process" subtitle="How we go from assessment to a scaling business." />
          <StepCards
            activeIndex={processStep}
            onSelect={setProcessStep}
            steps={[
              { title: 'Kickoff', text: 'A 1-on-1 meeting to build your roadmap and set up your client portal.' },
              { title: 'Roadmap', text: 'We build a customized roadmap tailored to your business goals and assessment results.' },
              { title: 'Scale', text: 'Ongoing 1-on-1 coaching and roadmap execution to achieve your growth goals.' },
            ]}
          />
          {/* Dummy walkthrough of the real portal screens for each step */}
          <div className="mt-6">
            <ProcessFlowPreview firstName={previewFirstName} stepIndex={processStep} onStepChange={setProcessStep} />
          </div>
        </section>

        {/* Suggested Program */}
        <section className={`${CARD} p-6 md:p-8 mb-6`}>
          <SectionHeader emoji="✨" title="Suggested Program" />
          {programsLoading ? (
            <div className="flex flex-col items-center py-8">
              <BBALoader size={56} />
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading programs…</p>
            </div>
          ) : (
            <>
              {programs && getOrderedPrograms().length > 0 && (
            <>
              {/* Billing cycle toggle */}
              {Object.keys(billingCycles).length > 1 && (
                <div className="flex justify-center mb-8">
                  <div className="inline-flex items-center gap-1 p-1 rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-600">
                    {Object.entries(billingCycles).map(([cycleKey, cycle]) => (
                      <button
                        key={cycleKey}
                        type="button"
                        onClick={() => setBillingCycle(cycleKey)}
                        className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          billingCycle === cycleKey
                            ? 'bg-red-600 text-white shadow-sm'
                            : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                        }`}
                      >
                        {cycle.name || cycleKey}
                        {cycle.discount > 0 && (
                          <span
                            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                              billingCycle === cycleKey ? 'bg-white/20 text-white' : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                            }`}
                          >
                            Save {cycle.discount}%
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1fr_auto_1fr_auto_1fr] gap-8 lg:gap-4 pt-3">
                {getOrderedPrograms().flatMap(([programKey, program], index) => {
                  const items = [];
                  if (index > 0) {
                    const starIndex = index - 1;
                    const isFilled = betweenStarsFilled[starIndex];
                    items.push(
                      <div key={`star-${index}`} className="hidden lg:flex items-center justify-center py-4">
                        <button
                          type="button"
                          onClick={() => setBetweenStarsFilled((prev) => ({ ...prev, [starIndex]: !prev[starIndex] }))}
                          className={`w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110 border ${
                            isFilled
                              ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/40 dark:border-amber-800'
                              : 'bg-white border-gray-200 dark:bg-gray-700 dark:border-gray-600'
                          }`}
                          title={isFilled ? 'Remove in-between suggestion' : 'Suggest in-between'}
                        >
                          <Star className={`w-4 h-4 ${isFilled ? 'text-amber-500 fill-amber-500' : 'text-gray-400 dark:text-gray-500'}`} />
                        </button>
                      </div>
                    );
                  }
                  items.push(
                    <div key={programKey} className="contents">
                      {(() => {
                        const validatedProgram = validateProgram(program);
                        const keyLower = programKey.toLowerCase();
                        const nameLower = (validatedProgram.name || '').toLowerCase();
                        const isPlusProgram = keyLower.includes('plus') || nameLower.includes('plus');
                        const isMostPopular = validatedProgram.isMostPopular;
                        const isSuggested = suggestedProgramKey === programKey;
                        return (
                          <div
                            key={programKey}
                            className={`relative overflow-visible rounded-2xl border bg-white dark:bg-gray-800 transition-all duration-200 h-full flex flex-col ${
                              isMostPopular
                                ? 'border-red-500 ring-2 ring-red-500/30 shadow-lg shadow-red-500/10'
                                : isSuggested
                                  ? 'border-amber-400 ring-2 ring-amber-400/30 shadow-md'
                                  : 'border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                          >
                            {(isMostPopular || isSuggested) && (
                              <div className="absolute -top-3.5 inset-x-0 flex justify-center gap-2 z-10">
                                {isMostPopular && (
                                  <span className="bg-red-600 text-white px-3 py-1 rounded-full text-[11px] font-semibold shadow-sm whitespace-nowrap">
                                    Most Popular
                                  </span>
                                )}
                                {isSuggested && (
                                  <span className="bg-amber-500 text-white px-3 py-1 rounded-full text-[11px] font-semibold shadow-sm whitespace-nowrap">
                                    Suggested
                                  </span>
                                )}
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSuggestedProgram(programKey);
                              }}
                              className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110 z-10 border ${
                                isSuggested
                                  ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/40 dark:border-amber-800'
                                  : 'bg-gray-50 border-gray-200 dark:bg-gray-700 dark:border-gray-600'
                              }`}
                              title={isSuggested ? 'Remove suggestion' : 'Mark as suggested'}
                            >
                              <Star className={`w-4 h-4 ${isSuggested ? 'text-amber-500 fill-amber-500' : 'text-gray-400 dark:text-gray-500'}`} />
                            </button>
                            <div className="p-7 flex flex-col h-full">
                              <div className="text-center mb-5 pt-2">
                                <h3 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white mb-1.5">{validatedProgram.name}</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">{validatedProgram.description}</p>
                              </div>
                              <div className="text-center mb-6">
                                {billingCycle === 'monthly' && (
                                  <div className="flex items-baseline justify-center gap-1">
                                    <span className="text-2xl font-bold text-gray-900 dark:text-white">$</span>
                                    <span className="text-5xl font-bold tracking-tight text-gray-900 dark:text-white">
                                      {getMonthlyPrice(validatedProgram.pricePerMonth)}
                                    </span>
                                    <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">/month</span>
                                  </div>
                                )}
                                {billingCycle === 'quarterly' && billingCycles?.quarterly && (
                                  <>
                                    <div className="flex items-baseline justify-center gap-1">
                                      <span className="text-2xl font-bold text-gray-900 dark:text-white">$</span>
                                      <span className="text-5xl font-bold tracking-tight text-gray-900 dark:text-white">
                                        {getQuarterlyPrice(validatedProgram.pricePerMonth)}
                                      </span>
                                      <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">/quarter</span>
                                    </div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">${getMonthlyPrice(validatedProgram.pricePerMonth)}/month</p>
                                  </>
                                )}
                                {billingCycle === 'annual' && billingCycles?.annual && (
                                  <>
                                    <div className="flex items-baseline justify-center gap-1">
                                      <span className="text-2xl font-bold text-gray-900 dark:text-white">$</span>
                                      <span className="text-5xl font-bold tracking-tight text-gray-900 dark:text-white">
                                        {getMonthlyPrice(validatedProgram.pricePerMonth)}
                                      </span>
                                      <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">/month</span>
                                    </div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">${getAnnualPrice(validatedProgram.pricePerMonth)}/year</p>
                                  </>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedProgramForDetail(validatedProgram);
                                  setSelectedTierForDetail(programKey);
                                  requestAnimationFrame(() => {
                                    requestAnimationFrame(() => {
                                      programDetailsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    });
                                  });
                                }}
                                className={`w-full py-3 px-6 rounded-xl font-semibold transition-all mb-6 mt-auto shadow-sm hover:shadow ${
                                  isPlusProgram
                                    ? 'bg-red-600 text-white hover:bg-red-700'
                                    : `bg-gradient-to-r ${getProgramColor(validatedProgram.name)} text-white hover:opacity-90`
                                }`}
                              >
                                See Details
                              </button>
                              {getShowcaseFeatures(programKey).length > 0 && (
                                <div className="space-y-2 flex-1">
                                  {getShowcaseFeatures(programKey).map((feature, idx) => {
                                    const item = availableItems.find((availItem) => availItem.name === feature.text);
                                    const itemIcon = item?.icon || '⭐';
                                    const hasFeatureDescription = feature.description?.trim();
                                    return (
                                      <button
                                        key={idx}
                                        type="button"
                                        onClick={() => {
                                          if (hasFeatureDescription) showShowcaseFeatureDetails(feature);
                                          else if (item) showItemDetails(item);
                                          else showShowcaseFeatureDetails(feature);
                                        }}
                                        className="w-full flex items-center gap-3 p-2.5 rounded-xl bg-gray-50 dark:bg-gray-900/60 border border-gray-100 dark:border-gray-700 hover:border-red-200 dark:hover:border-red-700 hover:bg-red-50/40 dark:hover:bg-red-900/20 transition-colors text-left"
                                      >
                                        <span className="w-8 h-8 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center text-base flex-shrink-0">
                                          {itemIcon}
                                        </span>
                                        <span className="text-sm text-gray-700 dark:text-gray-300">{feature.text}</span>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                              <p className="mt-4 text-center text-[11px] text-gray-400 dark:text-gray-500">Includes more than featured items above</p>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  );
                  return items;
                })}
              </div>
            </>
              )}
              {/* Program builder is always open: a saved proposal loads into it, otherwise a blank custom program */}
              <div ref={programDetailsSectionRef} id="program-details-section" className="mt-6 scroll-mt-8 w-full">
                <ProgramDetail
                  embedded={true}
                  selectedProgramProp={selectedProgramForDetail || CUSTOM_PROGRAM}
                  selectedTierProp={selectedProgramForDetail ? selectedTierForDetail : CUSTOM_TIER}
                  hideProgramDropdown={true}
                  hideHeader={true}
                  billingCycleProp={lead?.proposedProgram?.billingCycle ?? billingCycle}
                  checkoutPathProp={isCrmContext ? '/portal/checkout' : '/start/checkout'}
                  leadIdProp={leadId}
                  confirmMode={!!leadId}
                  proposedProgramData={lead?.proposedProgram}
                  initialSelectedDeliverables={lead?.proposedProgram?.selectedDeliverables ? new Set(lead.proposedProgram.selectedDeliverables) : null}
                  initialDeliverableQuantities={lead?.proposedProgram?.deliverableQuantities || null}
                  onConfirmProgram={async (data) => {
                    const docId = effectiveLeadIdRef.current || leadId;
                    console.log('[AssessmentAnalysis] onConfirmProgram called', { docId, leadId, effectiveLeadIdRef: effectiveLeadIdRef.current, dataKeys: Object.keys(data || {}) });
                    if (!docId || String(docId).startsWith('email:')) {
                      console.error('[AssessmentAnalysis] Invalid docId for save:', docId);
                      throw new Error('Invalid lead. Please open Assessment Analysis from the lead overview.');
                    }
                    const toSave = JSON.parse(JSON.stringify(data));
                    toSave.isProgramConfirmed = true;
                    toSave.confirmedAt = new Date().toISOString();
                    console.log('[AssessmentAnalysis] Saving to Firestore', { path: `accounts/${docId}`, toSave });
                    await updateAccount(docId, {
                      proposedProgram: toSave,
                      lastUpdated: new Date().toISOString()
                    });
                    console.log('[AssessmentAnalysis] Firestore save successful, updating local state');
                    setLead((prev) => (prev ? { ...prev, proposedProgram: { ...data, isProgramConfirmed: true, confirmedAt: toSave.confirmedAt } } : prev));
                  }}
                  onProgramEdited={async () => {
                    const docId = effectiveLeadIdRef.current || leadId;
                    console.log('[AssessmentAnalysis] onProgramEdited called', { docId, leadId });
                    if (!docId || String(docId).startsWith('email:')) return;
                    await updateAccount(docId, {
                      proposedProgram: null,
                      lastUpdated: new Date().toISOString()
                    });
                    setLead((prev) => (prev ? { ...prev, proposedProgram: null } : prev));
                  }}
                />
              </div>
            </>
          )}
        </section>

        {/* Getting Started */}
        <section className={`${CARD} p-6 md:p-8 mb-6`}>
          <SectionHeader emoji={PORTAL_EMOJI.onboarding} title="Getting Started" subtitle="Three quick steps from decision to an active portal." />
          <StepCards
            activeIndex={startStep}
            onSelect={setStartStep}
            steps={[
              { title: 'Pick a Start Date', text: "Choose when you'd like to begin your program." },
              { title: 'Sign Contract in Portal', text: 'Complete your contract signing through the client portal.' },
              { title: 'Portal Unlocks', text: "Your portal access is activated and you're ready to go." },
            ]}
          />
          {/* Dummy walkthrough of the real /start screens for each step */}
          <div className="mt-6">
            <StartFlowPreview
              stepIndex={startStep}
              onStepChange={setStartStep}
              companyName={lead?.company || 'Your Company'}
              signerName={previewSigner}
              programName={previewProgramName}
              price={previewPrice}
              billingText={previewBillingText}
              billingCycle={billingCycle}
              minimumTerm={previewProgram?.minimumTerm || 12}
              programCards={previewProgramCards}
            />
          </div>
        </section>

        {/* Emoji Picker Modal */}
        <ModalShell
          open={showEmojiPicker !== null}
          onClose={() => { setShowEmojiPicker(null); setEmojiSearch(''); }}
          emoji="😊"
          title="Select Emoji"
          maxWidth="max-w-3xl"
          bodyClass="flex flex-col min-h-0"
          footer={
            <button
              type="button"
              onClick={() => { setShowEmojiPicker(null); setEmojiSearch(''); }}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors"
            >
              Done
            </button>
          }
        >
          <input
            type="text"
            placeholder="Search emojis..."
            value={emojiSearch}
            onChange={(e) => setEmojiSearch(e.target.value)}
            className={`${INPUT} mb-4`}
          />
          <div className="overflow-y-auto max-h-[50vh]">
            <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
              {filteredEmojis.map((emoji, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    if (showEmojiPicker !== null) updateLeverCard(showEmojiPicker, 'emoji', emoji);
                    setShowEmojiPicker(null);
                    setEmojiSearch('');
                  }}
                  className={`aspect-square rounded-xl text-2xl flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    showEmojiPicker !== null && editableLevers[showEmojiPicker]?.emoji === emoji
                      ? 'bg-red-600 text-white'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </ModalShell>

        {/* Item Details Modal */}
        <ModalShell
          open={!!(showItemModal && selectedItem)}
          onClose={closeItemModal}
          emoji={selectedItem?.icon || '📦'}
          tone="gray"
          title={selectedItem?.name || 'Item Details'}
          subtitle={
            selectedItem?.department ? (
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getDepartmentColor(selectedItem.department)}`}>
                {selectedItem.department}
              </span>
            ) : null
          }
          footer={
            <button type="button" onClick={closeItemModal} className="px-4 py-2 rounded-lg text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white transition-colors">
              Close
            </button>
          }
        >
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5 text-center">Description</p>
          <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300 text-center">{selectedItem?.description}</p>
        </ModalShell>

        {/* Showcase Feature Details Modal */}
        <ModalShell
          open={!!selectedShowcaseFeature}
          onClose={closeShowcaseFeatureModal}
          emoji="⭐"
          tone="amber"
          title={selectedShowcaseFeature?.text || ''}
          footer={
            <button type="button" onClick={closeShowcaseFeatureModal} className="px-4 py-2 rounded-lg text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white transition-colors">
              Close
            </button>
          }
        >
          {selectedShowcaseFeature?.description?.trim() ? (
            <p className="text-sm leading-relaxed whitespace-pre-wrap text-gray-600 dark:text-gray-300 text-center">{selectedShowcaseFeature.description}</p>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">No description available.</p>
          )}
        </ModalShell>
      </div>
    </div>
  );
};

export default AssessmentAnalysis;
